import { useState, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Truck,
  HardHat,
  Receipt,
  Search,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  Calendar,
  Layers,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Filter,
  ShoppingBag,
  Edit2,
  X,
} from 'lucide-react';
import { toPaise, lineAmount, formatMoney, isAdmin } from '@vns/core';
import { useSession } from '../lib/session';
import Shell from '../components/Shell';
import Confirm, { useConfirm } from '../components/Confirm';
import {
  Drawer,
  Field,
  Select,
  TableSkeleton,
  EmptyState,
  ErrorState,
  StatusChip,
  formatDate,
  useToast,
} from '../components/ui';
import { useLookups, PAYMENT_MODES, normalizeUnitLabel } from '../lib/lookups';
import { useSubcontractorContracts, useCreateSubcontractor } from '../lib/directory';

const INDIVIDUAL_UNITS = [
  { value: 'Sq.ft', label: 'Sq.ft (Square Feet)' },
  { value: 'CFT', label: 'CFT (Cubic Feet)' },
  { value: 'RFT', label: 'RFT (Running Feet)' },
  { value: 'KG', label: 'KG (Kilogram)' },
  { value: 'No', label: 'No (Numbers / Pieces)' },
];
import {
  useClientTransactions,
  useProjectClientPaymentSummary,
  useRecordClientPayment,
  useUpdateClientPayment,
  useDeleteClientPayment,
  useSupplierTransactions,
  useSupplierBalanceSummary,
  useRecordSupplierPurchase,
  useUpdateSupplierPurchase,
  useDeleteSupplierTransaction,
  useSubcontractorTransactions,
  useSubcontractorBalanceSummary,
  useRecordSubcontractorLabour,
  useUpdateSubcontractorLabour,
  useDeleteSubcontractorTransaction,
  cleanWorkTypeName,
  useExpenseTransactions,
  useRecordExpense,
  useUpdateExpense,
  useDeleteExpenseTransaction,
  type ClientPaymentDraft,
  type SupplierPurchaseDraft,
  type SupplierMaterialLineDraft,
  type SubcontractorLabourDraft,
  type SubcontractorWorkLineDraft,
  type SubcontractorIndividualWorkLineDraft,
  type ExpenseDraft,
  type ClientTxRow,
  type SupplierTxRow,
  type SubcontractorTxRow,
  type ExpenseTxRow,
} from '../lib/transactions';

type TxType = 'clients' | 'suppliers' | 'subcontractors' | 'expenses';

interface TabMeta {
  key: TxType;
  label: string;
  sublabel: string;
  recordLabel: string;
  icon: typeof Users;
  color: string;
}

const TAB_MAP: Record<TxType, TabMeta> = {
  clients: {
    key: 'clients',
    label: 'Client',
    sublabel: 'Record client receipts, billings & milestone credits',
    recordLabel: 'Client payment',
    icon: Users,
    color: '#2563eb',
  },
  suppliers: {
    key: 'suppliers',
    label: 'Supplier',
    sublabel: 'Material purchase bills, itemized line entries & payments',
    recordLabel: 'Supplier or material payment',
    icon: Truck,
    color: '#059669',
  },
  subcontractors: {
    key: 'subcontractors',
    label: 'Sub contractor',
    sublabel: 'Contractor labour attendance, trade rates & disbursements',
    recordLabel: 'Sub contractor payment',
    icon: HardHat,
    color: '#d97706',
  },
  expenses: {
    key: 'expenses',
    label: 'Other Expenses',
    sublabel: 'Project site expenses, fuel, travel & operational overheads',
    recordLabel: 'Add expenses',
    icon: Receipt,
    color: '#7c3aed',
  },
};

const TABS: TabMeta[] = [
  TAB_MAP.clients,
  TAB_MAP.suppliers,
  TAB_MAP.subcontractors,
  TAB_MAP.expenses,
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function Transactions() {
  const { type = 'clients' } = useParams<{ type?: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const activeTab: TxType =
    type === 'suppliers' || type === 'subcontractors' || type === 'expenses'
      ? type
      : 'clients';

  const currentMeta = TAB_MAP[activeTab];
  const IconComponent = currentMeta.icon;

  const { data: lookups, isLoading: lookupsLoading } = useLookups();

  // Queries for each tab
  const clientQ = useClientTransactions(search, selectedProjectId);
  const supplierQ = useSupplierTransactions(search, selectedProjectId);
  const subcontractorQ = useSubcontractorTransactions(search, selectedProjectId);
  const { data: allSubcontractorContracts } = useSubcontractorContracts();
  const expenseQ = useExpenseTransactions(search, selectedProjectId);

  // Mutations
  const recordClientPay = useRecordClientPayment();
  const updateClientPay = useUpdateClientPayment();
  const deleteClientPay = useDeleteClientPayment();
  const recordSupplierPurchase = useRecordSupplierPurchase();
  const updateSupplierPurchase = useUpdateSupplierPurchase();
  const deleteSupplierTx = useDeleteSupplierTransaction();
  const recordSubcontractorLabour = useRecordSubcontractorLabour();
  const updateSubcontractorLabour = useUpdateSubcontractorLabour();
  const deleteSubcontractorTx = useDeleteSubcontractorTransaction();
  const recordExpense = useRecordExpense();
  const updateExpense = useUpdateExpense();
  const deleteExpenseTx = useDeleteExpenseTransaction();

  const actor = useSession((s) => s.actor);
  const admin = isAdmin(actor);

  const confirmDelete = useConfirm<{
    type: TxType;
    label: string;
    target: ClientTxRow | SupplierTxRow | SubcontractorTxRow | ExpenseTxRow;
  }>();

  const handleDeleteConfirm = async () => {
    if (!confirmDelete.target) return;
    const { type: targetType, target } = confirmDelete.target;
    try {
      if (targetType === 'clients') {
        await deleteClientPay.mutateAsync(target as ClientTxRow);
        toast.show('Client payment deleted successfully');
      } else if (targetType === 'suppliers') {
        await deleteSupplierTx.mutateAsync(target as SupplierTxRow);
        toast.show('Supplier transaction deleted successfully');
      } else if (targetType === 'subcontractors') {
        await deleteSubcontractorTx.mutateAsync(target as SubcontractorTxRow);
        toast.show('Sub contractor transaction deleted successfully');
      } else if (targetType === 'expenses') {
        await deleteExpenseTx.mutateAsync(target as ExpenseTxRow);
        toast.show('Expense deleted successfully');
      }
      confirmDelete.close();
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'Could not delete entry', true);
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);

  // -------------------------------------------------------------
  // DRAFT STATES
  // -------------------------------------------------------------
  // 1. Client Payment Draft
  const [clientDraft, setClientDraft] = useState<
    ClientPaymentDraft & {
      clientId?: string;
      creditRupees?: string;
      debitRupees?: string;
    }
  >({
    date: todayIso(),
    clientId: '',
    projectId: '',
    projectValuePaise: 0,
    txType: 'credit',
    amountRupees: '',
    creditRupees: '',
    debitRupees: '',
    mode: 'Cash',
    remarks: '',
  });

  // 2. Supplier Purchase Draft
  const [supplierDraft, setSupplierDraft] = useState<SupplierPurchaseDraft>({
    date: todayIso(),
    billDate: todayIso(),
    clientId: '',
    projectId: '',
    supplierId: '',
    txType: 'credit',
    billNo: '',
    printedTotalRupees: '',
    lines: [
      {
        material_id: '',
        unit_id: '',
        unit_name: '',
        quantity: '1',
        rateRupees: '',
        totalAmountPaise: 0,
      },
    ],
    paidAmountRupees: '',
    mode: 'Cash',
    remarks: '',
  });

  const createInitialSubcontractorDraft = (mode: 'weekly' | 'individual' = 'weekly'): SubcontractorLabourDraft => ({
    date: todayIso(),
    clientId: '',
    projectId: '',
    subcontractorId: '',
    txType: 'debit',
    entryMode: mode,
    nameOfWork: '',
    workVolume: '',
    unit: 'Sq.ft',
    rate: '',
    creditAmountRupees: '',
    lines: [
      {
        work_type_id: '',
        worker_count: '1',
        daily_rate: '',
        totalAmountPaise: 0,
        remarks: '',
      },
    ],
    individualLines: [
      {
        nameOfWork: '',
        workVolume: '',
        unit: 'Sq.ft',
        rate: '',
      },
    ],
    paidAmountRupees: '',
    mode: 'Cash',
    remarks: '',
  });

  // 3. Subcontractor Labour Draft
  const [subcontractorDraft, setSubcontractorDraft] = useState<SubcontractorLabourDraft>(() =>
    createInitialSubcontractorDraft('weekly'),
  );

  // 4. Expense Draft
  const [expenseDraft, setExpenseDraft] = useState<ExpenseDraft>({
    date: todayIso(),
    billDate: todayIso(),
    clientId: '',
    projectId: '',
    categoryId: '',
    customCategory: '',
    title: '',
    amountRupees: '',
    paidTo: '',
    billNo: '',
    mode: 'Cash',
    remarks: '',
  });

  // Quick Add Subcontractor (for individual mode)
  const createSubcontractor = useCreateSubcontractor();
  const [showQuickAddSub, setShowQuickAddSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [newSubPhone, setNewSubPhone] = useState('');
  const [newSubLocation, setNewSubLocation] = useState('');
  const [isCreatingSub, setIsCreatingSub] = useState(false);

  const handleCreateQuickSub = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newSubName.trim()) {
      toast.show('Please enter subcontractor name', true);
      return;
    }
    if (!newSubPhone.trim()) {
      toast.show('Please enter phone number', true);
      return;
    }
    try {
      setIsCreatingSub(true);
      const created = await createSubcontractor.mutateAsync({
        name: newSubName.trim(),
        phone: newSubPhone.trim(),
        city: newSubLocation.trim() || undefined,
        address: newSubLocation.trim() || undefined,
        workTypeIds: [],
      });
      // Set the newly created subcontractor as selected in the draft
      setSubcontractorDraft((prev) => ({ ...prev, subcontractorId: created.id }));
      // Reset form and close
      setNewSubName('');
      setNewSubPhone('');
      setNewSubLocation('');
      setShowQuickAddSub(false);
      toast.show('Sub contractor added successfully');
    } catch (err: any) {
      toast.show(err.message || 'Failed to add subcontractor', true);
    } finally {
      setIsCreatingSub(false);
    }
  };

  const toggleRowExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Quick lookup maps
  const projectMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; value_paise?: number; client_id?: string }>();
    lookups?.projects?.forEach((p) => map.set(p.id, p));
    return map;
  }, [lookups?.projects]);

  const materialMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; unit_id?: string | null; rate_paise?: number }>();
    lookups?.materials?.forEach((m) => map.set(m.id, m));
    return map;
  }, [lookups?.materials]);

  const unitMap = useMemo(() => {
    const map = new Map<string, { id: string; name: string; short_name: string }>();
    lookups?.units?.forEach((u) => map.set(u.id, u));
    return map;
  }, [lookups?.units]);

  // Project selector options for filters
  const projectFilterOptions = useMemo(() => {
    const base = [{ value: '', label: 'All Projects' }];
    if (!lookups?.projects) return base;
    return base.concat(lookups.projects.map((p) => ({ value: p.id, label: p.name })));
  }, [lookups?.projects]);

  // Filtered projects for each drawer based on selected client
  const supplierProjects = useMemo(() => {
    if (!supplierDraft.clientId) return lookups?.projects ?? [];
    return (lookups?.projects ?? []).filter((p) => p.client_id === supplierDraft.clientId);
  }, [lookups?.projects, supplierDraft.clientId]);

  const handleSupplierClientChange = (clientId: string) => {
    setSupplierDraft((prev) => {
      const currentProj = lookups?.projects?.find((p) => p.id === prev.projectId);
      const resetProject = clientId && currentProj && currentProj.client_id !== clientId;
      return {
        ...prev,
        clientId,
        projectId: resetProject ? '' : prev.projectId,
      };
    });
  };

  const handleSupplierProjectChange = (projectId: string) => {
    const proj = lookups?.projects?.find((p) => p.id === projectId);
    setSupplierDraft((prev) => ({
      ...prev,
      projectId,
      clientId: proj?.client_id ? proj.client_id : prev.clientId,
    }));
  };

  const subcontractorProjects = useMemo(() => {
    if (!subcontractorDraft.clientId) return lookups?.projects ?? [];
    return (lookups?.projects ?? []).filter((p) => p.client_id === subcontractorDraft.clientId);
  }, [lookups?.projects, subcontractorDraft.clientId]);

  const subcontractorWorkTypeOptions = useMemo(() => {
    const list = (lookups?.workTypes ?? [])
      .filter((w) => !/\s*\((?:Visit|Entry|Bill)\s*\d+\)/i.test(w.name))
      .map((w) => ({ value: w.id, label: cleanWorkTypeName(w.name) }));
    const hasOther = list.some((w) => w.label.trim().toLowerCase() === 'others' || w.label.trim().toLowerCase() === 'other');
    if (!hasOther) {
      list.push({ value: '__other__' as string, label: 'Others' });
    }
    return list;
  }, [lookups?.workTypes]);

  const handleSubcontractorClientChange = (clientId: string) => {
    setSubcontractorDraft((prev) => {
      const currentProj = lookups?.projects?.find((p) => p.id === prev.projectId);
      const resetProject = clientId && currentProj && currentProj.client_id !== clientId;
      return {
        ...prev,
        clientId,
        projectId: resetProject ? '' : prev.projectId,
      };
    });
  };

  const handleSubcontractorProjectChange = (projectId: string) => {
    const proj = lookups?.projects?.find((p) => p.id === projectId);
    setSubcontractorDraft((prev) => ({
      ...prev,
      projectId,
      clientId: proj?.client_id ? proj.client_id : prev.clientId,
    }));
  };

  const expenseProjects = useMemo(() => {
    if (!expenseDraft.clientId) return lookups?.projects ?? [];
    return (lookups?.projects ?? []).filter((p) => p.client_id === expenseDraft.clientId);
  }, [lookups?.projects, expenseDraft.clientId]);

  const handleExpenseClientChange = (clientId: string) => {
    setExpenseDraft((prev) => {
      const currentProj = lookups?.projects?.find((p) => p.id === prev.projectId);
      const resetProject = clientId && currentProj && currentProj.client_id !== clientId;
      return {
        ...prev,
        clientId,
        projectId: resetProject ? '' : prev.projectId,
      };
    });
  };

  const handleExpenseProjectChange = (projectId: string) => {
    const proj = lookups?.projects?.find((p) => p.id === projectId);
    setExpenseDraft((prev) => ({
      ...prev,
      projectId,
      clientId: proj?.client_id ? proj.client_id : prev.clientId,
    }));
  };

  // Filtered projects for client drawer based on selected client
  const clientProjects = useMemo(() => {
    if (!clientDraft.clientId) return lookups?.projects ?? [];
    return (lookups?.projects ?? []).filter((p) => p.client_id === clientDraft.clientId);
  }, [lookups?.projects, clientDraft.clientId]);

  const handleClientChange = (clientId: string) => {
    setClientDraft((prev) => {
      const currentProj = lookups?.projects?.find((p) => p.id === prev.projectId);
      const resetProject = clientId && currentProj && currentProj.client_id !== clientId;
      const nextProjectId = resetProject ? '' : prev.projectId;
      const nextProj = lookups?.projects?.find((p) => p.id === nextProjectId);
      return {
        ...prev,
        clientId,
        projectId: nextProjectId,
        projectValuePaise: Number(nextProj?.value_paise ?? 0),
      };
    });
  };

  // Handle Client Project change (auto-fills project value and client)
  const handleClientProjectChange = (projectId: string) => {
    const proj = projectMap.get(projectId);
    setClientDraft((prev) => ({
      ...prev,
      projectId,
      clientId: proj?.client_id ? proj.client_id : prev.clientId,
      projectValuePaise: Number(proj?.value_paise ?? 0),
    }));
  };

  // Supplier Draft line operations
  const handleSupplierMaterialChange = (idx: number, materialId: string) => {
    const mat = materialMap.get(materialId);
    setSupplierDraft((prev) => {
      const lines = [...prev.lines];
      const cur = lines[idx];
      if (!cur) return prev;
      const unit = mat?.unit_id ? unitMap.get(mat.unit_id) : undefined;
      const rateRupees = mat?.rate_paise ? (mat.rate_paise / 100).toString() : cur.rateRupees;
      const qtyNum = parseFloat(cur.quantity) || 0;
      const rateNum = parseFloat(rateRupees) || 0;
      const totalAmountPaise = Math.round(qtyNum * rateNum * 100);

      lines[idx] = {
        material_id: materialId,
        unit_id: mat?.unit_id || cur.unit_id || '',
        unit_name: unit?.short_name || unit?.name || '',
        quantity: cur.quantity,
        rateRupees,
        totalAmountPaise,
      };

      // update printed total if it wasn't manually typed differently
      const computedTotal = lines.reduce((s, l) => s + l.totalAmountPaise, 0);
      return {
        ...prev,
        lines,
        printedTotalRupees: (computedTotal / 100).toString(),
      };
    });
  };

  const handleSupplierLineChange = (
    idx: number,
    field: 'quantity' | 'unit_id' | 'rateRupees',
    value: string,
  ) => {
    setSupplierDraft((prev) => {
      const lines = [...prev.lines];
      const cur = lines[idx];
      if (!cur) return prev;

      let unitName = cur.unit_name;
      if (field === 'unit_id') {
        const u = unitMap.get(value);
        unitName = u?.short_name || u?.name || '';
      }

      const updated = {
        ...cur,
        [field]: value,
        unit_name: unitName,
      };

      const qty = parseFloat(updated.quantity) || 0;
      const rate = parseFloat(updated.rateRupees) || 0;
      updated.totalAmountPaise = Math.round(qty * rate * 100);
      lines[idx] = updated;

      const computedTotal = lines.reduce((s, l) => s + l.totalAmountPaise, 0);
      return {
        ...prev,
        lines,
        printedTotalRupees: (computedTotal / 100).toString(),
      };
    });
  };

  const addSupplierLine = () => {
    setSupplierDraft((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          material_id: '',
          unit_id: '',
          unit_name: '',
          quantity: '1',
          rateRupees: '',
          totalAmountPaise: 0,
        },
      ],
    }));
  };

  const removeSupplierLine = (idx: number) => {
    if (supplierDraft.lines.length <= 1) return;
    setSupplierDraft((prev) => {
      const lines = prev.lines.filter((_, i) => i !== idx);
      const computedTotal = lines.reduce((s, l) => s + l.totalAmountPaise, 0);
      return {
        ...prev,
        lines,
        printedTotalRupees: (computedTotal / 100).toString(),
      };
    });
  };

  // Subcontractor draft line operations
  const handleSubcontractorLineChange = (
    idx: number,
    field: 'work_type_id' | 'worker_count' | 'daily_rate' | 'remarks' | 'custom_work_name' | 'is_custom',
    value: any,
  ) => {
    setSubcontractorDraft((prev) => {
      const lines = [...prev.lines];
      const cur = lines[idx];
      if (!cur) return prev;

      const updated = {
        ...cur,
        [field]: value,
      };

      const rate = parseFloat(updated.daily_rate) || 0;
      updated.worker_count = '1';
      updated.totalAmountPaise = Math.round(rate * 100);
      lines[idx] = updated;
      return { ...prev, lines };
    });
  };

  const addSubcontractorLine = () => {
    setSubcontractorDraft((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          work_type_id: '',
          worker_count: '1',
          daily_rate: '',
          totalAmountPaise: 0,
          remarks: '',
          custom_work_name: '',
          is_custom: false,
        },
      ],
    }));
  };

  const removeSubcontractorLine = (idx: number) => {
    if (subcontractorDraft.lines.length <= 1) return;
    setSubcontractorDraft((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== idx),
    }));
  };

  // Individual work line operations
  const handleIndividualLineChange = (
    idx: number,
    field: 'nameOfWork' | 'workVolume' | 'unit' | 'rate',
    value: string,
  ) => {
    setSubcontractorDraft((prev) => {
      const currentLines =
        prev.individualLines && prev.individualLines.length > 0
          ? [...prev.individualLines]
          : [
              {
                nameOfWork: prev.nameOfWork || '',
                workVolume: prev.workVolume || '',
                unit: prev.unit || 'Sq.ft',
                rate: prev.rate || '',
              },
            ];
      const cur = currentLines[idx] || { nameOfWork: '', workVolume: '', unit: 'Sq.ft', rate: '' };
      currentLines[idx] = { ...cur, [field]: value };

      return {
        ...prev,
        individualLines: currentLines,
        ...(idx === 0
          ? {
              nameOfWork: currentLines[0]?.nameOfWork,
              workVolume: currentLines[0]?.workVolume,
              unit: currentLines[0]?.unit,
              rate: currentLines[0]?.rate,
            }
          : {}),
      };
    });
  };

  const addIndividualLine = () => {
    setSubcontractorDraft((prev) => {
      const currentLines =
        prev.individualLines && prev.individualLines.length > 0
          ? [...prev.individualLines]
          : [
              {
                nameOfWork: prev.nameOfWork || '',
                workVolume: prev.workVolume || '',
                unit: prev.unit || 'Sq.ft',
                rate: prev.rate || '',
              },
            ];
      return {
        ...prev,
        individualLines: [
          ...currentLines,
          {
            nameOfWork: '',
            workVolume: '',
            unit: 'Sq.ft',
            rate: '',
          },
        ],
      };
    });
  };

  const removeIndividualLine = (idx: number) => {
    setSubcontractorDraft((prev) => {
      const currentLines = prev.individualLines || [];
      if (currentLines.length <= 1) return prev;
      const updated = currentLines.filter((_, i) => i !== idx);
      return {
        ...prev,
        individualLines: updated,
        nameOfWork: updated[0]?.nameOfWork || '',
        workVolume: updated[0]?.workVolume || '',
        unit: updated[0]?.unit || 'Sq.ft',
        rate: updated[0]?.rate || '',
      };
    });
  };

  // Calculation helpers for drawers
  const computedSupplierTotalRupees = useMemo(() => {
    const totalPaise = supplierDraft.lines.reduce((s, l) => s + l.totalAmountPaise, 0);
    return totalPaise / 100;
  }, [supplierDraft.lines]);

  const supplierPrintedTotalNum = parseFloat(supplierDraft.printedTotalRupees) || computedSupplierTotalRupees;
  const supplierPaidNum = parseFloat(supplierDraft.paidAmountRupees) || 0;
  const supplierBalanceNum = Math.max(0, supplierPrintedTotalNum - supplierPaidNum);
  const excessSupplierPaidTowardsPrior = Math.max(0, supplierPaidNum - supplierPrintedTotalNum);

  // Supplier balance summary helpers
  const supplierBalanceSummaryQ = useSupplierBalanceSummary(
    supplierDraft.supplierId,
    supplierDraft.projectId,
  );
  const dbSupplierBalanceOwedPaise = supplierBalanceSummaryQ.data?.balanceOwedPaise ?? 0;
  const editingOriginalSupplierBill = editingId
    ? supplierBalanceSummaryQ.data?.billMap?.[editingId]
    : undefined;
  const editingOriginalSupplierPayment = editingId
    ? supplierBalanceSummaryQ.data?.paymentMap?.[editingId]
    : undefined;

  const priorSupplierBalancePaise = useMemo(() => {
    if (editingOriginalSupplierBill) {
      return Math.max(
        0,
        dbSupplierBalanceOwedPaise -
          editingOriginalSupplierBill.totalAmountPaise +
          editingOriginalSupplierBill.paidAmountPaise,
      );
    }
    if (editingOriginalSupplierPayment) {
      return dbSupplierBalanceOwedPaise + editingOriginalSupplierPayment.amountPaise;
    }
    return dbSupplierBalanceOwedPaise;
  }, [dbSupplierBalanceOwedPaise, editingOriginalSupplierBill, editingOriginalSupplierPayment]);

  const priorSupplierBalanceRupees = priorSupplierBalancePaise / 100;
  // (Previous Balance + This Bill Credit) - Total Debit Paid
  const netSupplierBalanceToGiveRupees = (priorSupplierBalanceRupees + supplierPrintedTotalNum) - supplierPaidNum;
  const totalSupplierBalanceToGiveRupees = Math.max(0, netSupplierBalanceToGiveRupees);
  const excessSupplierAdvanceRupees = Math.max(0, -netSupplierBalanceToGiveRupees);

  const netSupplierDebitRemaining = priorSupplierBalanceRupees - supplierPaidNum;
  const remainingSupplierDebitRupees = Math.max(0, netSupplierDebitRemaining);
  const excessSupplierDebitAdvance = Math.max(0, -netSupplierDebitRemaining);

  const subcontractorOverallTotalRupees = useMemo(() => {
    const totalPaise = subcontractorDraft.lines.reduce((s, l) => s + l.totalAmountPaise, 0);
    return totalPaise / 100;
  }, [subcontractorDraft.lines]);

  const subcontractorPaidNum = parseFloat(subcontractorDraft.paidAmountRupees) || 0;
  const subcontractorBalanceNum = Math.max(0, subcontractorOverallTotalRupees - subcontractorPaidNum);
  const excessSubcontractorPaidTowardsPrior = Math.max(0, subcontractorPaidNum - subcontractorOverallTotalRupees);

  // Subcontractor balance summary helpers
  const subcontractorBalanceSummaryQ = useSubcontractorBalanceSummary(
    subcontractorDraft.subcontractorId,
    subcontractorDraft.projectId,
  );
  const dbSubcontractorBalanceOwedPaise = subcontractorDraft.projectId
    ? (subcontractorBalanceSummaryQ.data?.projectBalanceOwedPaise ?? 0)
    : (subcontractorBalanceSummaryQ.data?.balanceOwedPaise ?? 0);
  const editingOriginalSubcontractorBatch = editingId
    ? subcontractorBalanceSummaryQ.data?.batchMap?.[editingId]
    : undefined;
  const editingOriginalSubcontractorPayment = editingId
    ? subcontractorBalanceSummaryQ.data?.paymentMap?.[editingId]
    : undefined;

  const priorSubcontractorBalancePaise = useMemo(() => {
    if (editingOriginalSubcontractorBatch) {
      return Math.max(
        0,
        dbSubcontractorBalanceOwedPaise -
          editingOriginalSubcontractorBatch.totalAmountPaise +
          editingOriginalSubcontractorBatch.paidAmountPaise,
      );
    }
    if (editingOriginalSubcontractorPayment) {
      return dbSubcontractorBalanceOwedPaise + editingOriginalSubcontractorPayment.amountPaise;
    }
    return dbSubcontractorBalanceOwedPaise;
  }, [
    dbSubcontractorBalanceOwedPaise,
    editingOriginalSubcontractorBatch,
    editingOriginalSubcontractorPayment,
  ]);

  const priorSubcontractorBalanceRupees = priorSubcontractorBalancePaise / 100;
  // (Previous Balance + This Work Credit) - Total Debit Paid
  const netSubcontractorBalanceToGiveRupees = (priorSubcontractorBalanceRupees + subcontractorOverallTotalRupees) - subcontractorPaidNum;
  const totalSubcontractorBalanceToGiveRupees = Math.max(0, netSubcontractorBalanceToGiveRupees);
  const excessSubcontractorAdvanceRupees = Math.max(0, -netSubcontractorBalanceToGiveRupees);

  const netSubcontractorDebitRemaining = priorSubcontractorBalanceRupees - subcontractorPaidNum;
  const remainingSubcontractorDebitRupees = Math.max(0, netSubcontractorDebitRemaining);
  const excessSubcontractorDebitAdvance = Math.max(0, -netSubcontractorDebitRemaining);

  const currentSubcontractorContract = useMemo(() => {
    if (!subcontractorDraft.subcontractorId || !subcontractorDraft.projectId) return null;
    return (
      allSubcontractorContracts?.find(
        (c) =>
          c.subcontractorId === subcontractorDraft.subcontractorId &&
          c.projectId === subcontractorDraft.projectId,
      ) ?? null
    );
  }, [allSubcontractorContracts, subcontractorDraft.subcontractorId, subcontractorDraft.projectId]);

  const individualTotalRupees = useMemo(() => {
    if (subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0) {
      return subcontractorDraft.individualLines.reduce((sum, item) => {
        const vol = parseFloat(item.workVolume || '0') || 0;
        const rate = parseFloat(item.rate || '0') || 0;
        return sum + vol * rate;
      }, 0);
    }
    const individualVolumeNum = parseFloat(subcontractorDraft.workVolume || '0') || 0;
    const individualRateNum = parseFloat(subcontractorDraft.rate || '0') || 0;
    return individualVolumeNum * individualRateNum;
  }, [subcontractorDraft.individualLines, subcontractorDraft.workVolume, subcontractorDraft.rate]);
  const indPaidNum = parseFloat(subcontractorDraft.paidAmountRupees || '0') || 0;
  const indBalanceNum = Math.max(0, individualTotalRupees - indPaidNum);
  const netIndBalanceToGiveRupees = priorSubcontractorBalanceRupees + individualTotalRupees - indPaidNum;
  const totalIndBalanceToGiveRupees = Math.max(0, netIndBalanceToGiveRupees);

  // Client payment drawer calculation helpers
  const projectPaymentSummaryQ = useProjectClientPaymentSummary(clientDraft.projectId);
  const selectedClientProj = lookups?.projects?.find((p) => p.id === clientDraft.projectId);
  const clientProjectValuePaise = clientDraft.projectValuePaise || Number(selectedClientProj?.value_paise || 0);

  const dbTotalReceivedPaise = projectPaymentSummaryQ.data?.totalReceivedPaise ?? 0;
  const editingOriginalRecord = editingId ? projectPaymentSummaryQ.data?.paymentMap?.[editingId] : undefined;

  const priorReceivedPaise = useMemo(() => {
    if (!editingOriginalRecord) return dbTotalReceivedPaise;
    const origEffective = editingOriginalRecord.isDebit
      ? -editingOriginalRecord.amountPaise
      : editingOriginalRecord.amountPaise;
    return Math.max(0, dbTotalReceivedPaise - origEffective);
  }, [dbTotalReceivedPaise, editingOriginalRecord]);

  const currentBalanceNeedToGetPaise = clientProjectValuePaise - priorReceivedPaise;
  const clientPrevBalanceRupees = Math.max(0, currentBalanceNeedToGetPaise / 100);

  const clientCreditNum = parseFloat(clientDraft.creditRupees || (clientDraft.txType === 'credit' ? clientDraft.amountRupees : '') || '0') || 0;
  const clientDebitNum = parseFloat(clientDraft.debitRupees || (clientDraft.txType === 'debit' ? clientDraft.amountRupees : '') || '0') || 0;
  const clientBillBalanceRupees = Math.max(0, clientCreditNum - clientDebitNum);
  const clientPaidRupees = clientDebitNum > 0 ? clientDebitNum : clientCreditNum;
  const netClientBalanceNeedToGet = clientPrevBalanceRupees - clientPaidRupees;
  const clientTotalBalanceNeedToGetRupees = Math.max(0, netClientBalanceNeedToGet);
  const hasEnteredAmount = clientCreditNum > 0 || clientDebitNum > 0;
  const remainingBalanceAfterThisPaise = Math.round(netClientBalanceNeedToGet * 100);

  // Open fresh Add Drawer
  const handleOpenAdd = () => {
    setEditingId(null);
    if (activeTab === 'clients') {
      setClientDraft({
        date: todayIso(),
        clientId: '',
        projectId: '',
        projectValuePaise: 0,
        txType: 'credit',
        amountRupees: '',
        creditRupees: '',
        debitRupees: '',
        mode: 'Cash',
        remarks: '',
      });
    } else if (activeTab === 'suppliers') {
      setSupplierDraft({
        date: todayIso(),
        billDate: todayIso(),
        clientId: '',
        projectId: '',
        supplierId: '',
        txType: 'credit',
        billNo: '',
        printedTotalRupees: '',
        lines: [
          {
            material_id: '',
            unit_id: '',
            unit_name: '',
            quantity: '1',
            rateRupees: '',
            totalAmountPaise: 0,
          },
        ],
        paidAmountRupees: '',
        mode: 'Cash',
        remarks: '',
      });
    } else if (activeTab === 'subcontractors') {
      setSubcontractorDraft(createInitialSubcontractorDraft('weekly'));
    } else if (activeTab === 'expenses') {
      setExpenseDraft({
        date: todayIso(),
        billDate: todayIso(),
        clientId: '',
        projectId: '',
        categoryId: '',
        customCategory: '',
        title: '',
        amountRupees: '',
        paidTo: '',
        billNo: '',
        mode: 'Cash',
        remarks: '',
      });
    }
    setDrawerOpen(true);
  };

  // Open Edit Drawers
  const handleEditClient = (r: ClientTxRow) => {
    setEditingId(r.id);
    const isDebit = r.debitPaise > 0;
    const amtPaise = isDebit ? r.debitPaise : r.creditPaise;
    const proj = projectMap.get(r.projectId);
    setClientDraft({
      date: r.date,
      clientId: proj?.client_id || '',
      projectId: r.projectId,
      projectValuePaise: r.projectValuePaise,
      txType: isDebit ? 'debit' : 'credit',
      amountRupees: (amtPaise / 100).toString(),
      creditRupees: r.creditPaise > 0 ? (r.creditPaise / 100).toString() : '',
      debitRupees: r.debitPaise > 0 ? (r.debitPaise / 100).toString() : '',
      mode: r.mode || 'Cash',
      remarks: r.remarks || '',
    });
    setDrawerOpen(true);
  };

  const handleEditSupplier = (r: SupplierTxRow) => {
    setEditingId(r.id);
    const isDebit = r.totalAmountPaise === 0 && r.paidAmountPaise > 0;
    const proj = projectMap.get(r.projectId);
    const lines: SupplierMaterialLineDraft[] = r.lines.length
      ? r.lines.map((l) => ({
          material_id: l.materialId,
          unit_id: l.unitId || '',
          unit_name: l.unit || '',
          quantity: l.quantity.toString(),
          rateRupees: (l.ratePaise / 100).toString(),
          totalAmountPaise: l.amountPaise,
        }))
      : [
          {
            material_id: '',
            unit_id: '',
            unit_name: '',
            quantity: '1',
            rateRupees: '',
            totalAmountPaise: 0,
          },
        ];

    setSupplierDraft({
      date: r.date,
      billDate: r.date,
      clientId: proj?.client_id || '',
      projectId: r.projectId,
      supplierId: r.supplierId,
      txType: isDebit ? 'debit' : 'credit',
      billNo: r.billNo || '',
      printedTotalRupees: (r.totalAmountPaise / 100).toString(),
      lines,
      paidAmountRupees: r.paidAmountPaise > 0 ? (r.paidAmountPaise / 100).toString() : '',
      mode: r.mode || 'Cash',
      remarks: r.remarks || '',
    });
    setDrawerOpen(true);
  };

  const handleEditSubcontractor = (r: SubcontractorTxRow) => {
    setEditingId(r.id);
    const isDebit = r.overallTotalPaise === 0 && r.paidAmountPaise > 0;
    const proj = projectMap.get(r.projectId);

    // Detect if this was recorded as an individual piece-rate entry
    const indMatch = (r.remarks || r.lines[0]?.remarks || '').match(
      /\[Individual:\s*([\d.]+)\s*([\w.]+)\s*@\s*₹?([\d.]+)\/([^\]\s]+)\](?:\s*(.*))?/,
    );
    const isIndividual = Boolean(indMatch || (r.remarks || '').includes('[Individual'));

    const individualLines: SubcontractorIndividualWorkLineDraft[] = [];
    if (isIndividual && r.lines.length > 0) {
      r.lines.forEach((l) => {
        const lineIndMatch = (l.remarks || r.remarks || '').match(
          /\[Individual:\s*([\d.]+)\s*([\w.]+)\s*@\s*₹?([\d.]+)\/([^\]\s]+)\](?:\s*(.*))?/,
        );
        if (lineIndMatch) {
          individualLines.push({
            nameOfWork: l.workTypeName || '',
            workVolume: lineIndMatch[1] || '',
            unit: lineIndMatch[2] || 'Sq.ft',
            rate: lineIndMatch[3] || '',
          });
        } else {
          individualLines.push({
            nameOfWork: l.workTypeName || '',
            workVolume: '1',
            unit: 'Sq.ft',
            rate: l.totalAmountPaise > 0 ? (l.totalAmountPaise / 100).toString() : '',
          });
        }
      });
    }
    if (individualLines.length === 0) {
      individualLines.push({
        nameOfWork: isIndividual ? r.lines[0]?.workTypeName || '' : '',
        workVolume: isIndividual && indMatch ? indMatch[1] || '' : '',
        unit: isIndividual && indMatch ? indMatch[2] || 'Sq.ft' : 'Sq.ft',
        rate: isIndividual && indMatch ? indMatch[3] || '' : '',
      });
    }

    const lines: SubcontractorWorkLineDraft[] = r.lines.length
      ? r.lines.map((l) => {
          const wt = lookups?.workTypes?.find((w) => w.name === l.workTypeName);
          return {
            work_type_id: wt?.id || '',
            worker_count: '1',
            daily_rate: l.totalAmountPaise > 0
              ? (l.totalAmountPaise / 100).toString()
              : l.ratePerWorkerPaise > 0
              ? (l.ratePerWorkerPaise / 100).toString()
              : '',
            totalAmountPaise: l.totalAmountPaise,
            remarks: l.remarks || '',
          };
        })
      : [
          {
            work_type_id: '',
            worker_count: '1',
            daily_rate: '',
            totalAmountPaise: 0,
            remarks: '',
          },
        ];

    setSubcontractorDraft({
      date: r.date,
      clientId: proj?.client_id || '',
      projectId: r.projectId,
      subcontractorId: r.subcontractorId,
      txType: isDebit ? 'debit' : 'credit',
      entryMode: isIndividual ? 'individual' : 'weekly',
      nameOfWork: individualLines[0]?.nameOfWork || '',
      workVolume: individualLines[0]?.workVolume || '',
      unit: individualLines[0]?.unit || 'Sq.ft',
      rate: individualLines[0]?.rate || '',
      creditAmountRupees: !isDebit && r.overallTotalPaise > 0 ? (r.overallTotalPaise / 100).toString() : '',
      lines,
      individualLines,
      paidAmountRupees: r.paidAmountPaise > 0 ? (r.paidAmountPaise / 100).toString() : '',
      mode: 'Cash',
      remarks: isIndividual && indMatch && indMatch[5] ? indMatch[5] : r.remarks || '',
    });
    setDrawerOpen(true);
  };

  const handleEditExpense = (r: ExpenseTxRow) => {
    setEditingId(r.id);
    const proj = projectMap.get(r.projectId);
    const matchedCategory = lookups?.expenseCategories?.find((c) => c.id === r.categoryId);
    const isOtherCategory =
      !matchedCategory &&
      (r.categoryId === '__other__' || !r.categoryId || (r.categoryName && r.categoryName !== 'General Expense'));

    const customCategoryName = isOtherCategory
      ? (r.categoryName && r.categoryName !== 'General Expense' ? r.categoryName : r.title && r.title !== 'Expense' ? r.title : '')
      : '';

    setExpenseDraft({
      date: r.date,
      billDate: r.date,
      clientId: proj?.client_id || '',
      projectId: r.projectId,
      categoryId: isOtherCategory ? '__other__' : (r.categoryId || ''),
      customCategory: customCategoryName,
      title: r.title,
      amountRupees: (r.amountPaise / 100).toString(),
      paidTo: r.paidTo || '',
      billNo: r.billNo || '',
      mode: 'Cash',
      remarks: r.remarks || '',
    });
    setDrawerOpen(true);
  };

  // Form submit handler (Add & Edit)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (activeTab === 'clients') {
        if (!clientDraft.projectId) throw new Error('Please select a project');
        const creditAmt = parseFloat(clientDraft.creditRupees || '0') || 0;
        const debitAmt = parseFloat(clientDraft.debitRupees || '0') || 0;
        const fallbackAmt = parseFloat(clientDraft.amountRupees || '0') || 0;

        if (creditAmt <= 0 && debitAmt <= 0 && fallbackAmt <= 0) {
          throw new Error('Please enter amount');
        }

        const paidAmt = debitAmt > 0 ? debitAmt : (creditAmt > 0 ? creditAmt : fallbackAmt);

        if (editingId) {
          const draftToUpdate: ClientPaymentDraft = {
            ...clientDraft,
            amountRupees: paidAmt.toString(),
            txType: 'credit',
          };
          await updateClientPay.mutateAsync({ id: editingId, draft: draftToUpdate });
          toast.show('Client transaction updated successfully');
        } else {
          await recordClientPay.mutateAsync({
            ...clientDraft,
            amountRupees: paidAmt.toString(),
            txType: 'credit',
            remarks: clientDraft.remarks.trim(),
          });
          toast.show('Client transaction recorded successfully');
        }
        setClientDraft({
          date: todayIso(),
          clientId: '',
          projectId: '',
          projectValuePaise: 0,
          txType: 'credit',
          amountRupees: '',
          creditRupees: '',
          debitRupees: '',
          mode: 'Cash',
          remarks: '',
        });
      } else if (activeTab === 'suppliers') {
        if (!supplierDraft.projectId) throw new Error('Please select a project');
        if (!supplierDraft.supplierId) throw new Error('Please select a supplier');

        const creditNum = parseFloat(supplierDraft.printedTotalRupees || '0') || 0;
        const debitNum = parseFloat(supplierDraft.paidAmountRupees || '0') || 0;
        const hasValidLines = supplierDraft.lines.some(
          (l) => l.material_id && Number(l.quantity) > 0,
        );

        if (creditNum <= 0 && debitNum <= 0 && !hasValidLines) {
          throw new Error('Please enter either Credit (purchase bill) or Debit (paid amount)');
        }

        const draftToSubmit: SupplierPurchaseDraft = {
          ...supplierDraft,
          txType: hasValidLines || creditNum > 0 ? 'credit' : 'debit',
        };

        if (editingId) {
          await updateSupplierPurchase.mutateAsync({ id: editingId, draft: draftToSubmit });
          toast.show(
            draftToSubmit.txType === 'debit'
              ? 'Supplier payment (Debit) updated successfully'
              : 'Material purchase bill updated successfully',
          );
        } else {
          await recordSupplierPurchase.mutateAsync(draftToSubmit);
          toast.show(
            draftToSubmit.txType === 'debit'
              ? 'Supplier payment (Debit) recorded successfully'
              : 'Material purchase bill recorded successfully',
          );
        }
        setSupplierDraft({
          date: todayIso(),
          billDate: todayIso(),
          clientId: '',
          projectId: '',
          supplierId: '',
          txType: 'credit',
          billNo: '',
          printedTotalRupees: '',
          lines: [
            {
              material_id: '',
              unit_id: '',
              unit_name: '',
              quantity: '1',
              rateRupees: '',
              totalAmountPaise: 0,
            },
          ],
          paidAmountRupees: '',
          mode: 'Cash',
          remarks: '',
        });
      } else if (activeTab === 'subcontractors') {
        if (!subcontractorDraft.projectId) throw new Error('Please select a project');
        if (!subcontractorDraft.subcontractorId) throw new Error('Please select a sub contractor');

        if (subcontractorDraft.entryMode === 'individual') {
          const lines =
            subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0
              ? subcontractorDraft.individualLines
              : [
                  {
                    nameOfWork: subcontractorDraft.nameOfWork || '',
                    workVolume: subcontractorDraft.workVolume || '',
                    unit: subcontractorDraft.unit || 'Sq.ft',
                    rate: subcontractorDraft.rate || '',
                  },
                ];

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (!line || !line.nameOfWork?.trim()) {
              throw new Error(`Please enter the name of work for item #${i + 1}`);
            }
            const volume = parseFloat(line.workVolume || '0') || 0;
            if (volume <= 0) throw new Error(`Please enter a valid work volume for item #${i + 1}`);
            const rate = parseFloat(line.rate || '0') || 0;
            if (rate <= 0) throw new Error(`Please enter a valid rate per unit for item #${i + 1}`);
          }
        } else {
          // Weekly mode
          const hasLabourLines = subcontractorDraft.lines.some(
            (l) => (l.work_type_id || (l.is_custom && l.custom_work_name?.trim())) && parseFloat(l.daily_rate || '0') > 0,
          );
          const hasDebit = parseFloat(subcontractorDraft.paidAmountRupees || '0') > 0;
          if (!hasLabourLines && !hasDebit) {
            throw new Error('Please enter work name and weekly rate (Credit) or a paid amount (Debit)');
          }
        }

        const hasCredit =
          subcontractorDraft.entryMode === 'individual'
            ? individualTotalRupees > 0
            : subcontractorDraft.lines.some(
                (l) => (l.work_type_id || (l.is_custom && l.custom_work_name?.trim())) && parseFloat(l.daily_rate || '0') > 0,
              );

        const draftToSubmit: SubcontractorLabourDraft = {
          ...subcontractorDraft,
          txType: hasCredit ? 'credit' : 'debit',
        };

        if (editingId) {
          await updateSubcontractorLabour.mutateAsync({ id: editingId, draft: draftToSubmit });
          toast.show(
            subcontractorDraft.entryMode === 'individual'
              ? 'Individual work entry updated successfully'
              : draftToSubmit.txType === 'debit'
              ? 'Sub contractor payment (Debit) updated successfully'
              : 'Sub contractor labour entry updated successfully',
          );
        } else {
          await recordSubcontractorLabour.mutateAsync(draftToSubmit);
          toast.show(
            subcontractorDraft.entryMode === 'individual'
              ? 'Individual work entry recorded successfully'
              : draftToSubmit.txType === 'debit'
              ? 'Sub contractor payment (Debit) recorded successfully'
              : 'Sub contractor labour entry recorded successfully',
          );
        }
        setSubcontractorDraft({
          date: todayIso(),
          clientId: '',
          projectId: '',
          subcontractorId: '',
          txType: 'debit',
          entryMode: 'weekly',
          nameOfWork: '',
          workVolume: '',
          unit: 'Sq.ft',
          rate: '',
          creditAmountRupees: '',
          lines: [
            {
              work_type_id: '',
              worker_count: '1',
              daily_rate: '',
              totalAmountPaise: 0,
              remarks: '',
            },
          ],
          individualLines: [
            {
              nameOfWork: '',
              workVolume: '',
              unit: 'Sq.ft',
              rate: '',
            },
          ],
          paidAmountRupees: '',
          mode: 'Cash',
          remarks: '',
        });
      } else if (activeTab === 'expenses') {
        if (!expenseDraft.projectId) throw new Error('Please select a project');
        if (!expenseDraft.amountRupees) throw new Error('Please enter amount');

        const isOther = expenseDraft.categoryId === '__other__';
        if (isOther && !expenseDraft.customCategory?.trim()) {
          throw new Error('Please enter expense category name');
        }

        const categoryName = isOther
          ? expenseDraft.customCategory?.trim()
          : lookups?.expenseCategories?.find((c) => c.id === expenseDraft.categoryId)?.name;

        const resolvedTitle = expenseDraft.title?.trim() || categoryName || expenseDraft.remarks?.trim() || 'Expense';

        const draftToSave: ExpenseDraft = {
          ...expenseDraft,
          categoryId: isOther ? '__other__' : expenseDraft.categoryId,
          customCategory: isOther ? expenseDraft.customCategory?.trim() : '',
          title: resolvedTitle,
        };

        if (editingId) {
          await updateExpense.mutateAsync({ id: editingId, draft: draftToSave });
          toast.show('Expense updated successfully');
        } else {
          await recordExpense.mutateAsync(draftToSave);
          toast.show('Expense recorded successfully');
        }
        setExpenseDraft({
          date: todayIso(),
          billDate: todayIso(),
          clientId: '',
          projectId: '',
          categoryId: '',
          customCategory: '',
          title: '',
          amountRupees: '',
          paidTo: '',
          billNo: '',
          mode: 'Cash',
          remarks: '',
        });
      }
      setEditingId(null);
      setDrawerOpen(false);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Error saving transaction', true);
    }
  };

  const isSaving =
    recordClientPay.isPending ||
    updateClientPay.isPending ||
    recordSupplierPurchase.isPending ||
    updateSupplierPurchase.isPending ||
    recordSubcontractorLabour.isPending ||
    updateSubcontractorLabour.isPending ||
    recordExpense.isPending ||
    updateExpense.isPending;

  // -------------------------------------------------------------
  // RENDER KPI CALCULATIONS
  // -------------------------------------------------------------
  const clientRows = clientQ.data ?? [];
  const supplierRows = supplierQ.data ?? [];
  const subcontractorRows = subcontractorQ.data ?? [];
  const expenseRows = expenseQ.data ?? [];

  return (
    <Shell
      title={`Transactions — ${currentMeta.label}`}
      crumb="Transaction"
    >

      {/* Filter, Search & Action Toolbar */}
      <div className="toolbar transactions-toolbar">
        <div className="field-inline transactions-search-box">
          <Search size={14} style={{ color: 'var(--text-faint)' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              activeTab === 'clients'
                ? 'Search client transactions by project, reference...'
                : activeTab === 'suppliers'
                ? 'Search supplier bills by invoice, materials, supplier...'
                : activeTab === 'subcontractors'
                ? 'Search sub-contractor labour by trade, workers...'
                : 'Search expenses by category, voucher, remarks...'
            }
            aria-label="Search transactions"
          />
        </div>

        <div className="transactions-filter-project">
          <Select
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            options={projectFilterOptions}
          />
        </div>

        <button
          type="button"
          className="btn btn-primary btn-auto transactions-add-btn"
          onClick={handleOpenAdd}
        >
          <Plus size={15} strokeWidth={2.4} />
          <span>{currentMeta.recordLabel}</span>
        </button>
      </div>

      {/* MAIN DATA TABLES */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        {/* 1. CLIENT TABLE */}
        {activeTab === 'clients' && (
          <>
            {clientQ.isLoading ? (
              <TableSkeleton cols={8} rows={6} />
            ) : clientQ.isError ? (
              <ErrorState error={clientQ.error} onRetry={() => void clientQ.refetch()} />
            ) : clientRows.length === 0 ? (
              <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <EmptyState
                  icon="users"
                  title="No client payments found"
                  hint={search ? 'Try clearing your search filter' : 'Click "Client payment" to post a client receipt or debit.'}
                />
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Date</th>
                        <th>Project Name</th>
                        <th>Client</th>
                        <th style={{ textAlign: 'right' }}>Project Value</th>
                        <th style={{ textAlign: 'right', color: '#059669' }}>Credit (₹)</th>
                        <th style={{ textAlign: 'right', color: '#dc2626' }}>Debit (₹)</th>
                        <th>Mode</th>
                        <th>Remarks</th>
                        <th style={{ width: 90 }}>Status</th>
                        <th style={{ width: admin ? 80 : 60, textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientRows.map((r) => (
                        <tr key={r.id}>
                          <td style={{ fontSize: 12.5, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                            {formatDate(r.date)}
                          </td>
                          <td style={{ fontWeight: 600, color: 'var(--text)' }}>
                            {r.projectName}
                          </td>
                          <td style={{ color: 'var(--text-soft)' }}>
                            {r.clientName}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                            {formatMoney(r.projectValuePaise)}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 600,
                              fontFamily: 'var(--font-mono)',
                              color: r.creditPaise > 0 ? '#059669' : 'var(--text-faint)',
                            }}
                          >
                            {r.creditPaise > 0 ? formatMoney(r.creditPaise) : '—'}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 600,
                              fontFamily: 'var(--font-mono)',
                              color: r.debitPaise > 0 ? '#dc2626' : 'var(--text-faint)',
                            }}
                          >
                            {r.debitPaise > 0 ? formatMoney(r.debitPaise) : '—'}
                          </td>
                          <td>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-xs)',
                                background: 'var(--surface-2)',
                                fontSize: 11.5,
                                fontWeight: 500,
                                textTransform: 'uppercase',
                              }}
                            >
                              {r.mode}
                            </span>
                          </td>
                          <td style={{ fontSize: 12.5, color: 'var(--text-soft)', maxWidth: 220 }}>
                            {r.remarks || '—'}
                          </td>
                          <td>
                            <StatusChip value={r.status} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleEditClient(r)}
                                className="icon-btn"
                                title="Edit payment"
                                style={{ width: 28, height: 28, padding: 0 }}
                              >
                                <Edit2 size={13} />
                              </button>
                              {admin && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    confirmDelete.ask({
                                      type: 'clients',
                                      label: `client payment for ${r.projectName} (${r.clientName})`,
                                      target: r,
                                    })
                                  }
                                  className="icon-btn btn-danger"
                                  title="Delete payment"
                                  style={{ width: 28, height: 28, padding: 0 }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* 2. SUPPLIER TABLE */}
        {activeTab === 'suppliers' && (
          <>
            {supplierQ.isLoading ? (
              <TableSkeleton cols={9} rows={6} />
            ) : supplierQ.isError ? (
              <ErrorState error={supplierQ.error} onRetry={() => void supplierQ.refetch()} />
            ) : supplierRows.length === 0 ? (
              <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <EmptyState
                  icon="truck"
                  title="No supplier purchase bills found"
                  hint={search ? 'Try clearing your search filter' : 'Click "Supplier or material payment" to add a new supplier bill.'}
                />
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th style={{ width: 110 }}>Date</th>
                        <th>Project</th>
                        <th>Supplier</th>
                        <th>Invoice / Bill No</th>
                        <th>Materials Purchased</th>
                        <th style={{ textAlign: 'right', color: '#2563eb' }}>Credit (₹)</th>
                        <th style={{ textAlign: 'right', color: '#059669' }}>Debit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Balance Due (₹)</th>
                        <th>Mode</th>
                        <th style={{ width: 90 }}>Status</th>
                        <th style={{ width: admin ? 80 : 60, textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierRows.map((r) => {
                        const isExpanded = expandedRows.has(r.id);
                        const isZeroBalance = r.balanceAmountPaise <= 0;
                        return (
                          <Fragment key={r.id}>
                            <tr style={{ background: isExpanded ? 'var(--surface-2)' : undefined }}>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(r.id)}
                                  className="icon-btn"
                                  style={{ width: 26, height: 26, padding: 0 }}
                                  title="Expand lines"
                                >
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              </td>
                              <td style={{ fontSize: 12.5, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                                {formatDate(r.date)}
                              </td>
                              <td style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {r.projectName}
                              </td>
                              <td style={{ fontWeight: 500, color: 'var(--text-soft)' }}>
                                {r.supplierName}
                              </td>
                              <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                {r.billNo ? (
                                  <span style={{ background: 'var(--surface-3)', padding: '2px 6px', borderRadius: 4 }}>
                                    {r.billNo}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                <div style={{ fontSize: 12.5 }}>
                                  <span style={{ fontWeight: 500 }}>{r.materialsSummary}</span>
                                  {r.linesCount > 0 && (
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        fontSize: 11,
                                        background: 'rgba(37, 99, 235, 0.1)',
                                        color: '#2563eb',
                                        padding: '1px 6px',
                                        borderRadius: 10,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {r.linesCount} items
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                                {formatMoney(r.totalAmountPaise)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', color: '#2563eb' }}>
                                {formatMoney(r.paidAmountPaise)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-xs)',
                                    fontSize: 12,
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: 600,
                                    background: isZeroBalance ? 'rgba(5, 150, 105, 0.12)' : 'rgba(217, 119, 6, 0.12)',
                                    color: isZeroBalance ? '#059669' : '#d97706',
                                  }}
                                >
                                  {isZeroBalance ? '₹0 (Paid in Full)' : formatMoney(r.balanceAmountPaise)}
                                </span>
                              </td>
                              <td style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--text-soft)' }}>
                                {r.mode}
                              </td>
                              <td>
                                <StatusChip value={r.status} />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleEditSupplier(r)}
                                    className="icon-btn"
                                    title="Edit purchase bill"
                                    style={{ width: 28, height: 28, padding: 0 }}
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  {admin && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        confirmDelete.ask({
                                          type: 'suppliers',
                                          label: `purchase bill ${r.billNo || r.projectName}`,
                                          target: r,
                                        })
                                      }
                                      className="icon-btn text-danger"
                                      title="Delete purchase bill"
                                      style={{ width: 28, height: 28, padding: 0 }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Nested Line Item Breakdown */}
                            {isExpanded && (
                              <tr key={`${r.id}-expanded`} style={{ background: 'var(--surface-2)' }}>
                                <td colSpan={12} style={{ padding: '12px 20px 16px 44px' }}>
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      border: '1px solid var(--border)',
                                      borderRadius: 'var(--radius-sm)',
                                      overflow: 'hidden',
                                      boxShadow: 'var(--shadow-xs)',
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: '8px 14px',
                                        background: 'var(--surface-3)',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: 'var(--text-soft)',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <span>Itemized Materials on Bill {r.billNo || ''}</span>
                                      {r.remarks && <span>Remarks: {r.remarks}</span>}
                                    </div>
                                    <table style={{ margin: 0 }}>
                                      <thead>
                                        <tr style={{ background: '#fafafa', borderBottom: '1px solid var(--border)' }}>
                                          <th style={{ fontSize: 11.5 }}>Material</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'right' }}>Quantity</th>
                                          <th style={{ fontSize: 11.5 }}>Unit</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'right' }}>Rate (₹)</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'right' }}>Amount</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {r.lines.map((l, i) => (
                                          <tr key={i}>
                                            <td style={{ fontWeight: 500, fontSize: 12.5 }}>{l.materialName}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {l.quantity}
                                            </td>
                                            <td style={{ fontSize: 12, color: 'var(--text-soft)' }}>{l.unit}</td>
                                            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {formatMoney(l.ratePaise)}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {formatMoney(l.amountPaise)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* 3. SUB CONTRACTOR TABLE */}
        {activeTab === 'subcontractors' && (
          <>
            {subcontractorQ.isLoading ? (
              <TableSkeleton cols={9} rows={6} />
            ) : subcontractorQ.isError ? (
              <ErrorState error={subcontractorQ.error} onRetry={() => void subcontractorQ.refetch()} />
            ) : subcontractorRows.length === 0 ? (
              <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <EmptyState
                  icon="hardhat"
                  title="No sub contractor labour entries found"
                  hint={search ? 'Try clearing your search filter' : 'Click "Sub contractor payment" to add a sub contractor visit.'}
                />
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}></th>
                        <th style={{ width: 110 }}>Date</th>
                        <th>Project</th>
                        <th>Sub contractor</th>
                        <th>Work Trades</th>
                        <th style={{ textAlign: 'center' }}>Total Workers</th>
                        <th style={{ textAlign: 'right', color: '#2563eb' }}>Credit (₹)</th>
                        <th style={{ textAlign: 'right', color: '#059669' }}>Debit (₹)</th>
                        <th style={{ textAlign: 'right' }}>Balance Due (₹)</th>
                        <th>Remarks</th>
                        <th style={{ width: 90 }}>Status</th>
                        <th style={{ width: admin ? 80 : 60, textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subcontractorRows.map((r) => {
                        const isExpanded = expandedRows.has(r.id);
                        const isZeroBalance = r.balanceAmountPaise <= 0;
                        return (
                          <Fragment key={r.id}>
                            <tr style={{ background: isExpanded ? 'var(--surface-2)' : undefined }}>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}>
                                <button
                                  type="button"
                                  onClick={() => toggleRowExpanded(r.id)}
                                  className="icon-btn"
                                  style={{ width: 26, height: 26, padding: 0 }}
                                  title="Expand trade lines"
                                >
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              </td>
                              <td style={{ fontSize: 12.5, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                                {formatDate(r.date)}
                              </td>
                              <td style={{ fontWeight: 600, color: 'var(--text)' }}>
                                {r.projectName}
                              </td>
                              <td style={{ fontWeight: 500, color: 'var(--text-soft)' }}>
                                {r.subcontractorName}
                              </td>
                              <td style={{ fontSize: 12.5 }}>
                                <span style={{ fontWeight: 500 }}>{r.tradesSummary}</span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    background: 'rgba(217, 119, 6, 0.1)',
                                    color: '#d97706',
                                  }}
                                >
                                  {r.totalWorkers} workers
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                                {formatMoney(r.overallTotalPaise)}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', color: '#059669' }}>
                                {formatMoney(r.paidAmountPaise)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <span
                                  style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-xs)',
                                    fontSize: 12,
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: 600,
                                    background: isZeroBalance ? 'rgba(5, 150, 105, 0.12)' : 'rgba(220, 38, 38, 0.12)',
                                    color: isZeroBalance ? '#059669' : '#dc2626',
                                  }}
                                >
                                  {isZeroBalance ? '₹0' : formatMoney(r.balanceAmountPaise)}
                                </span>
                              </td>
                              <td style={{ fontSize: 12.5, color: 'var(--text-soft)', maxWidth: 180 }}>
                                {r.remarks || '—'}
                              </td>
                              <td>
                                <StatusChip value={r.status} />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleEditSubcontractor(r)}
                                    className="icon-btn"
                                    title="Edit labour entry"
                                    style={{ width: 28, height: 28, padding: 0 }}
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  {admin && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        confirmDelete.ask({
                                          type: 'subcontractors',
                                          label: `sub contractor entry ${r.subcontractorName} (${r.tradesSummary})`,
                                          target: r,
                                        })
                                      }
                                      className="icon-btn text-danger"
                                      title="Delete labour entry"
                                      style={{ width: 28, height: 28, padding: 0 }}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Nested Trade Breakdown */}
                            {isExpanded && (
                              <tr key={`${r.id}-expanded`} style={{ background: 'var(--surface-2)' }}>
                                <td colSpan={12} style={{ padding: '12px 20px 16px 44px' }}>
                                  <div
                                    style={{
                                      background: '#ffffff',
                                      border: '1px solid var(--border)',
                                      borderRadius: 'var(--radius-sm)',
                                      overflow: 'hidden',
                                      boxShadow: 'var(--shadow-xs)',
                                    }}
                                  >
                                    <div
                                      style={{
                                        padding: '8px 14px',
                                        background: 'var(--surface-3)',
                                        borderBottom: '1px solid var(--border)',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: 'var(--text-soft)',
                                      }}
                                    >
                                      Work Trade Headcounts & Daily Rates
                                    </div>
                                    <table style={{ margin: 0 }}>
                                      <thead>
                                        <tr style={{ background: '#fafafa', borderBottom: '1px solid var(--border)' }}>
                                          <th style={{ fontSize: 11.5 }}>Work Trade</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'center' }}>Workers</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'right' }}>Daily Rate (₹)</th>
                                          <th style={{ fontSize: 11.5, textAlign: 'right' }}>Total Amount</th>
                                          <th style={{ fontSize: 11.5 }}>Remarks</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {r.lines.map((l, i) => (
                                          <tr key={i}>
                                            <td style={{ fontWeight: 600, fontSize: 12.5 }}>{cleanWorkTypeName(l.workTypeName)}</td>
                                            <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {l.workerCount}
                                            </td>
                                            <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {l.ratePerWorkerPaise > 0 ? formatMoney(l.ratePerWorkerPaise) : '—'}
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                                              {formatMoney(l.totalAmountPaise)}
                                            </td>
                                            <td style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                                              {l.remarks || '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* 4. EXPENSE TABLE */}
        {activeTab === 'expenses' && (
          <>
            {expenseQ.isLoading ? (
              <TableSkeleton cols={8} rows={6} />
            ) : expenseQ.isError ? (
              <ErrorState error={expenseQ.error} onRetry={() => void expenseQ.refetch()} />
            ) : expenseRows.length === 0 ? (
              <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
                <EmptyState
                  icon="receipt"
                  title="No other expenses recorded"
                  hint={search ? 'Try clearing your search filter' : 'Click "Add expenses" to record site petty cash or operational overheads.'}
                />
              </div>
            ) : (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>Date</th>
                        <th>Project</th>
                        <th>Category</th>
                        <th>Title / Description</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                        <th>Paid To</th>
                        <th>Bill No</th>
                        <th>Remarks / Mode</th>
                        <th style={{ width: 90 }}>Status</th>
                        <th style={{ width: admin ? 80 : 60, textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenseRows.map((r) => (
                        <tr key={r.id}>
                          <td style={{ fontSize: 12.5, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>
                            {formatDate(r.date)}
                          </td>
                          <td style={{ fontWeight: 600, color: 'var(--text)' }}>
                            {r.projectName}
                          </td>
                          <td>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: 'var(--radius-xs)',
                                background: 'rgba(124, 58, 237, 0.08)',
                                color: '#7c3aed',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {r.categoryName}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500 }}>
                            {r.title && r.title !== r.categoryName && r.title !== 'Expense' ? r.title : (r.remarks || r.categoryName || '—')}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                            {formatMoney(r.amountPaise)}
                          </td>
                          <td style={{ color: 'var(--text-soft)', fontSize: 12.5 }}>
                            {r.paidTo || '—'}
                          </td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                            {r.billNo ? (
                              <span style={{ background: 'var(--surface-3)', padding: '2px 6px', borderRadius: 4 }}>
                                {r.billNo}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td style={{ fontSize: 12.5, color: 'var(--text-soft)', maxWidth: 220 }}>
                            {r.remarks || '—'}
                          </td>
                          <td>
                            <StatusChip value={r.status} />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleEditExpense(r)}
                                className="icon-btn"
                                title="Edit expense"
                                style={{ width: 28, height: 28, padding: 0 }}
                              >
                                <Edit2 size={13} />
                              </button>
                              {admin && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    confirmDelete.ask({
                                      type: 'expenses',
                                      label: `expense ${r.title} (${r.categoryName})`,
                                      target: r,
                                    })
                                  }
                                  className="icon-btn text-danger"
                                  title="Delete expense"
                                  style={{ width: 28, height: 28, padding: 0 }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* ======================================================================
       * DRAWERS / MODALS FOR TRANSACTION CREATION
       * ====================================================================== */}
      {drawerOpen && (
        <>
          {/* 1. Client / Record Payment Drawer */}
          {activeTab === 'clients' && (
            <Drawer
              title={editingId ? 'Edit Client Payment' : 'Record Client Payment'}
              onClose={() => setDrawerOpen(false)}
              onSubmit={handleSubmit}
              submitLabel={editingId ? 'Save Changes' : 'Save Payment'}
              busy={isSaving}
            >
              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label="Date">
                  <input
                    type="date"
                    value={clientDraft.date}
                    onChange={(e) => setClientDraft((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </Field>

                <Field label="Select Client">
                  <Select
                    value={clientDraft.clientId || ''}
                    onChange={handleClientChange}
                    options={lookups?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []}
                    placeholder="Choose client…"
                  />
                </Field>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Field label="Select Project">
                  <Select
                    value={clientDraft.projectId}
                    onChange={handleClientProjectChange}
                    options={clientProjects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Choose project…"
                  />
                </Field>
              </div>

              {/* Project Financial Overview: Total Value, Received Till Date, Balance Need to Get & Live Remaining */}
              {clientDraft.projectId ? (
                <div
                  style={{
                    margin: '14px 0 16px',
                    padding: '14px 16px',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface-sunken, #f8fafc)',
                    border: '1px solid var(--border)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}
                >
                  {/* Row 1: Project Contract Value */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingBottom: 10,
                      borderBottom: '1px solid var(--border-soft, #e2e8f0)',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Project Contract Value
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-soft, #64748b)' }}>
                        Total agreed project value
                      </div>
                    </div>
                    <div style={{ fontSize: 16.5, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--text, #0f172a)' }}>
                      {clientProjectValuePaise > 0 ? formatMoney(clientProjectValuePaise) : '—'}
                    </div>
                  </div>

                  {/* Row 2: Received Till Date vs Balance Need to Get */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 10,
                      marginTop: 10,
                    }}
                  >
                    <div
                      style={{
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'rgba(5, 150, 105, 0.06)',
                        border: '1px solid rgba(5, 150, 105, 0.2)',
                      }}
                    >
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Received (Till Date)
                      </div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#065f46', marginTop: 2 }}>
                        {formatMoney(priorReceivedPaise)}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-sm)',
                        background: currentBalanceNeedToGetPaise > 0 ? 'rgba(217, 119, 6, 0.08)' : 'rgba(5, 150, 105, 0.06)',
                        border: currentBalanceNeedToGetPaise > 0 ? '1px solid rgba(217, 119, 6, 0.25)' : '1px solid rgba(5, 150, 105, 0.2)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: currentBalanceNeedToGetPaise > 0 ? '#b45309' : '#047857',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}
                      >
                        Previous Balance Need to Get
                      </div>
                      <div
                        style={{
                          fontSize: 14.5,
                          fontWeight: 700,
                          fontFamily: 'var(--font-mono)',
                          color: currentBalanceNeedToGetPaise > 0 ? '#b45309' : '#047857',
                          marginTop: 2,
                        }}
                      >
                        {currentBalanceNeedToGetPaise > 0
                          ? formatMoney(currentBalanceNeedToGetPaise)
                          : currentBalanceNeedToGetPaise === 0
                          ? '₹0.00 (Cleared)'
                          : `${formatMoney(Math.abs(currentBalanceNeedToGetPaise))} (Surplus)`}
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Live Remaining Balance after this transaction if amount entered */}
                  {hasEnteredAmount && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-sm)',
                        background: remainingBalanceAfterThisPaise <= 0 ? 'rgba(5, 150, 105, 0.09)' : 'rgba(37, 99, 235, 0.07)',
                        border: remainingBalanceAfterThisPaise <= 0 ? '1px solid rgba(5, 150, 105, 0.3)' : '1px solid rgba(37, 99, 235, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: remainingBalanceAfterThisPaise <= 0 ? '#065f46' : '#1e40af' }}>
                          Remaining Balance After This {clientDraft.txType === 'credit' ? 'Payment' : 'Debit'}:
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-soft, #64748b)' }}>
                          {clientDraft.txType === 'credit' ? 'Reduces receivable balance' : 'Debit adjustment / refund'}
                        </div>
                      </div>
                      <div style={{ fontSize: 15.5, fontWeight: 800, fontFamily: 'var(--font-mono)', color: remainingBalanceAfterThisPaise <= 0 ? '#047857' : '#1d4ed8' }}>
                        {remainingBalanceAfterThisPaise > 0
                          ? formatMoney(remainingBalanceAfterThisPaise)
                          : remainingBalanceAfterThisPaise === 0
                          ? '₹0.00 (Fully Cleared)'
                          : `${formatMoney(Math.abs(remainingBalanceAfterThisPaise))} (Advance Surplus)`}
                      </div>
                    </div>
                  )}

                  {editingOriginalRecord && (
                    <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--text-soft, #64748b)', textAlign: 'right' }}>
                      * Editing existing entry ({formatMoney(editingOriginalRecord.amountPaise)})
                    </div>
                  )}
                </div>
              ) : null}

              {/* Debit & Credit Section */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 14,
                  marginTop: 12,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                  Debit & Credit
                </div>

                <div className="grid3" style={{ gap: 10 }}>
                  <Field label="Credit (₹)">
                    <input
                      type="number"
                      step="any"
                      value={clientDraft.creditRupees || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setClientDraft((prev) => ({
                          ...prev,
                          creditRupees: val,
                          amountRupees: val || prev.debitRupees || '',
                          txType: val ? 'credit' : prev.debitRupees ? 'debit' : 'credit',
                        }));
                      }}
                      style={{
                        width: '100%',
                        height: 34,
                        fontWeight: 600,
                        fontFamily: 'var(--font-mono)',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: '0 10px',
                        background: '#ffffff',
                        fontSize: 13,
                      }}
                    />
                  </Field>

                  <Field label="Debit (₹)" optional>
                    <input
                      type="number"
                      step="any"
                      value={clientDraft.debitRupees || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setClientDraft((prev) => ({
                          ...prev,
                          debitRupees: val,
                          amountRupees: val || prev.creditRupees || '',
                          txType: val ? 'debit' : prev.creditRupees ? 'credit' : 'debit',
                        }));
                      }}
                      style={{
                        width: '100%',
                        height: 34,
                        fontWeight: 600,
                        fontFamily: 'var(--font-mono)',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        padding: '0 10px',
                        background: '#ffffff',
                        fontSize: 13,
                      }}
                    />
                  </Field>

                  <Field label="This Bill Balance (₹)">
                    <div
                      style={{
                        height: 34,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 10px',
                        borderRadius: 6,
                        border: clientBillBalanceRupees === 0 ? '1px solid #bbf7d0' : '1px solid #bfdbfe',
                        background: clientBillBalanceRupees === 0 ? '#f0fdf4' : '#eff6ff',
                        color: clientBillBalanceRupees === 0 ? '#059669' : '#1d4ed8',
                        fontWeight: 700,
                        fontSize: 13,
                        fontFamily: 'var(--font-mono)',
                        width: '100%',
                      }}
                    >
                      ₹{clientBillBalanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </Field>
                </div>

                {/* Total Balance Need to Get Box */}
                <div
                  style={{
                    marginTop: 10,
                    padding: '8px 12px',
                    borderRadius: 6,
                    background: clientTotalBalanceNeedToGetRupees > 0 ? '#eff6ff' : '#f0fdf4',
                    border: clientTotalBalanceNeedToGetRupees > 0 ? '1px solid #bfdbfe' : '1px solid #bbf7d0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: clientTotalBalanceNeedToGetRupees > 0 ? '#1e40af' : '#166534', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                    {netClientBalanceNeedToGet < 0
                      ? 'ADVANCE / SURPLUS FROM CLIENT'
                      : 'TOTAL BALANCE NEED TO GET (FROM CLIENT)'}
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      fontFamily: 'var(--font-mono)',
                      color: clientTotalBalanceNeedToGetRupees > 0 ? '#1d4ed8' : '#15803d',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {netClientBalanceNeedToGet < 0
                      ? `₹${Math.abs(netClientBalanceNeedToGet).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Surplus)`
                      : `₹${clientTotalBalanceNeedToGetRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Field label="Mode of Payment">
                  <Select
                    value={clientDraft.mode}
                    onChange={(v) => setClientDraft((prev) => ({ ...prev, mode: v }))}
                    options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
                  />
                </Field>
              </div>

              <Field label="Remarks" optional>
                <textarea
                  rows={2}
                  value={clientDraft.remarks}
                  onChange={(e) => setClientDraft((prev) => ({ ...prev, remarks: e.target.value }))}
                />
              </Field>
            </Drawer>
          )}

          {/* 2. Supplier / Material Purchase Drawer */}
          {activeTab === 'suppliers' && (
            <Drawer
              title={
                editingId
                  ? 'Edit Supplier Transaction'
                  : supplierDraft.txType === 'credit'
                  ? 'Record Material Purchase (Credit)'
                  : 'Record Supplier Payment (Debit)'
              }
              onClose={() => setDrawerOpen(false)}
              onSubmit={handleSubmit}
              submitLabel={
                editingId
                  ? 'Save Changes'
                  : supplierDraft.txType === 'credit'
                  ? 'Save Purchase Bill'
                  : 'Save Payment'
              }
              busy={isSaving}
              wide
            >
              {/* Header Info: Date 1st, Select Client 2nd, Select Project 3rd, Select Supplier 4th */}
              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label="Date">
                  <input
                    type="date"
                    value={supplierDraft.date}
                    onChange={(e) => setSupplierDraft((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </Field>

                <Field label="Select Client">
                  <Select
                    value={supplierDraft.clientId || ''}
                    onChange={handleSupplierClientChange}
                    options={lookups?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []}
                    placeholder="Choose client…"
                  />
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label="Select Project">
                  <Select
                    value={supplierDraft.projectId}
                    onChange={handleSupplierProjectChange}
                    options={supplierProjects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Choose project…"
                  />
                </Field>

                <Field label="Select Supplier">
                  <Select
                    value={supplierDraft.supplierId}
                    onChange={(v) => setSupplierDraft((prev) => ({ ...prev, supplierId: v }))}
                    options={lookups?.suppliers?.map((s) => ({ value: s.id, label: s.name })) ?? []}
                    placeholder="Choose supplier…"
                  />
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label={supplierDraft.txType === 'debit' ? 'Reference / Receipt No' : 'Invoice / Bill No'} optional>
                  <input
                    value={supplierDraft.billNo}
                    onChange={(e) => setSupplierDraft((prev) => ({ ...prev, billNo: e.target.value }))}
                  />
                </Field>

                <Field label="Bill or Invoice Date" optional>
                  <input
                    type="date"
                    value={supplierDraft.billDate || supplierDraft.date}
                    onChange={(e) => setSupplierDraft((prev) => ({ ...prev, billDate: e.target.value }))}
                  />
                </Field>
              </div>

              {/* Materials Section Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <ShoppingBag size={18} strokeWidth={2.2} style={{ color: '#0f172a' }} />
                    <h4 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      Materials on this Purchase Bill
                    </h4>
                  </div>

                  {/* Materials Card Container */}
                  <div
                    style={{
                      background: '#ffffff',
                      borderRadius: 10,
                      border: '1px solid #e2e8f0',
                      padding: '16px 18px',
                      marginBottom: 18,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                    }}
                  >
                    {/* Column Headers (Desktop Only) */}
                    <div className="material-table-header">
                      <div>MATERIAL ITEM</div>
                      <div style={{ textAlign: 'center' }}>QUANTITY</div>
                      <div style={{ textAlign: 'center' }}>UNIT</div>
                      <div style={{ textAlign: 'center' }}>RATE (₹)</div>
                      <div style={{ textAlign: 'center' }}>LINE AMOUNT</div>
                      <div />
                    </div>

                    {/* Line Items */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10 }}>
                      {supplierDraft.lines.map((line, idx) => (
                        <div key={idx} className="material-line-item">
                          {/* Row 1 on Mobile: Material Selector + Mobile Remove Button */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <label className="material-line-mobile-label">
                                Material Item
                              </label>
                              <Select
                                size="sm"
                                value={line.material_id}
                                placeholder="Choose a material"
                                options={[
                                  { value: '', label: 'Choose a material' },
                                  ...(lookups?.materials?.map((m) => ({ value: m.id, label: m.name })) ?? []),
                                ]}
                                onChange={(matId) => handleSupplierMaterialChange(idx, matId)}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSupplierLine(idx)}
                              disabled={supplierDraft.lines.length <= 1}
                              className="material-remove-btn-mobile"
                              title="Remove line"
                            >
                              <Trash2 size={16} strokeWidth={1.8} />
                            </button>
                          </div>

                          {/* Row 2 on Mobile: Quantity + Unit */}
                          <div className="material-line-col2">
                            <div>
                              <label className="material-line-mobile-label">
                                Quantity
                              </label>
                              <input
                                type="number"
                                step="any"
                                value={line.quantity}
                                onChange={(e) => handleSupplierLineChange(idx, 'quantity', e.target.value)}
                                style={{
                                  width: '100%',
                                  height: 38,
                                  textAlign: 'center',
                                  background: '#ffffff',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  fontFamily: 'var(--font-mono)',
                                  color: '#0f172a',
                                  padding: '0 8px',
                                }}
                                required
                              />
                            </div>

                            <div>
                              <label className="material-line-mobile-label">
                                Unit
                              </label>
                              <Select
                                size="sm"
                                value={line.unit_id}
                                placeholder="—"
                                options={[
                                  { value: '', label: '—' },
                                  ...(lookups?.units?.map((u) => ({ value: u.id, label: normalizeUnitLabel(u.name, u.short_name) })) ?? []),
                                ]}
                                onChange={(val) => handleSupplierLineChange(idx, 'unit_id', val)}
                              />
                            </div>
                          </div>

                          {/* Row 3 on Mobile: Rate + Line Amount */}
                          <div className="material-line-col2">
                            <div>
                              <label className="material-line-mobile-label">
                                Rate (₹)
                              </label>
                              <input
                                type="number"
                                step="any"
                                value={line.rateRupees}
                                onChange={(e) => handleSupplierLineChange(idx, 'rateRupees', e.target.value)}
                                style={{
                                  width: '100%',
                                  height: 38,
                                  textAlign: 'center',
                                  background: '#ffffff',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: 6,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  fontFamily: 'var(--font-mono)',
                                  color: '#0f172a',
                                  padding: '0 8px',
                                }}
                              />
                            </div>

                            <div>
                              <label className="material-line-mobile-label">
                                Line Amount
                              </label>
                              <div
                                style={{
                                  textAlign: 'center',
                                  fontFamily: 'var(--font-mono)',
                                  fontWeight: line.totalAmountPaise > 0 ? 700 : 500,
                                  fontSize: 13,
                                  color: line.totalAmountPaise > 0 ? '#0f172a' : '#94a3b8',
                                  height: 38,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: '#f8fafc',
                                  borderRadius: 6,
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                {line.totalAmountPaise === 0 ? '—' : formatMoney(line.totalAmountPaise)}
                              </div>
                            </div>
                          </div>

                          {/* Desktop Only Remove Button */}
                          <div className="material-remove-btn-desktop">
                            <button
                              type="button"
                              onClick={() => removeSupplierLine(idx)}
                              disabled={supplierDraft.lines.length <= 1}
                              style={{
                                width: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'none',
                                border: 'none',
                                cursor: supplierDraft.lines.length <= 1 ? 'not-allowed' : 'pointer',
                                color: '#94a3b8',
                                borderRadius: 4,
                                opacity: supplierDraft.lines.length <= 1 ? 0.3 : 1,
                                transition: 'color 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (supplierDraft.lines.length > 1) {
                                  e.currentTarget.style.color = '#ef4444';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#94a3b8';
                              }}
                              title="Remove line"
                            >
                              <Trash2 size={16} strokeWidth={1.8} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add Another Material Line Button */}
                    <div style={{ marginTop: 14 }}>
                      <button
                        type="button"
                        onClick={addSupplierLine}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          color: '#2563eb',
                          borderRadius: 8,
                          padding: '8px 16px',
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#dbeafe';
                          e.currentTarget.style.borderColor = '#93c5fd';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#eff6ff';
                          e.currentTarget.style.borderColor = '#bfdbfe';
                        }}
                      >
                        <Plus size={15} strokeWidth={2.4} />
                        <span>Add Another Material Line</span>
                      </button>
                    </div>
                  </div>

                  {/* Debit & Credit Section */}
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '12px 14px',
                      marginBottom: 14,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                      Debit & Credit
                    </div>

                    {/* Previous Balance Banner */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        borderRadius: 6,
                        background: priorSupplierBalanceRupees > 0 ? '#fffbeb' : '#f0fdf4',
                        border: priorSupplierBalanceRupees > 0 ? '1px solid #fde68a' : '1px solid #bbf7d0',
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700, color: priorSupplierBalanceRupees > 0 ? '#92400e' : '#166534', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Previous Balance
                      </div>
                      <div
                        style={{
                          fontSize: 14.5,
                          fontWeight: 800,
                          fontFamily: 'var(--font-mono)',
                          color: priorSupplierBalanceRupees > 0 ? '#b45309' : '#15803d',
                        }}
                      >
                        ₹{priorSupplierBalanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    <div className="grid3" style={{ gap: 10 }}>
                      <Field label="Credit (₹)">
                        <input
                          type="number"
                          step="any"
                          value={supplierDraft.printedTotalRupees}
                          onChange={(e) => setSupplierDraft((prev) => ({ ...prev, printedTotalRupees: e.target.value }))}
                          style={{
                            width: '100%',
                            height: 34,
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '0 10px',
                            background: '#ffffff',
                            fontSize: 13,
                          }}
                        />
                      </Field>

                      <Field label="Debit (₹)">
                        <input
                          type="number"
                          step="any"
                          value={supplierDraft.paidAmountRupees}
                          onChange={(e) => setSupplierDraft((prev) => ({ ...prev, paidAmountRupees: e.target.value }))}
                          style={{
                            width: '100%',
                            height: 34,
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            color: '#2563eb',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '0 10px',
                            background: '#ffffff',
                            fontSize: 13,
                          }}
                        />
                      </Field>

                      <Field label="This Bill Balance (₹)">
                        <div
                          style={{
                            height: 34,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                            borderRadius: 6,
                            border: supplierBalanceNum === 0 ? '1px solid #bbf7d0' : '1px solid #fde68a',
                            background: supplierBalanceNum === 0 ? '#f0fdf4' : '#fffbeb',
                            color: supplierBalanceNum === 0 ? '#059669' : '#d97706',
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: 'var(--font-mono)',
                            width: '100%',
                          }}
                        >
                          ₹{supplierBalanceNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </Field>
                    </div>

                    {/* Total Balance Need to Give Box */}
                    <div
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        borderRadius: 6,
                        background: totalSupplierBalanceToGiveRupees > 0 ? '#eff6ff' : '#f0fdf4',
                        border: totalSupplierBalanceToGiveRupees > 0 ? '1px solid #bfdbfe' : '1px solid #bbf7d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700, color: totalSupplierBalanceToGiveRupees > 0 ? '#1e40af' : '#166534', letterSpacing: '0.02em' }}>
                        {netSupplierBalanceToGiveRupees < 0
                          ? 'ADVANCE / SURPLUS TO SUPPLIER'
                          : 'TOTAL BALANCE NEED TO GIVE (PAY TO SUPPLIER)'}
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 800,
                          fontFamily: 'var(--font-mono)',
                          color: totalSupplierBalanceToGiveRupees > 0 ? '#1d4ed8' : '#15803d',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {netSupplierBalanceToGiveRupees < 0
                          ? `₹${excessSupplierAdvanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Advance)`
                          : `₹${totalSupplierBalanceToGiveRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </div>
                    </div>

                    {editingOriginalSupplierBill && (
                      <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--text-soft, #64748b)', textAlign: 'right' }}>
                        * Editing existing bill entry
                      </div>
                    )}
              </div>

              {/* Mode & Remarks */}
              <div className="grid2" style={{ gap: 14 }}>
                <Field label="Mode of Transaction">
                  <Select
                    value={supplierDraft.mode}
                    onChange={(v) => setSupplierDraft((prev) => ({ ...prev, mode: v }))}
                    options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
                  />
                </Field>

                <Field label="Remarks" optional>
                  <input
                    value={supplierDraft.remarks}
                    onChange={(e) => setSupplierDraft((prev) => ({ ...prev, remarks: e.target.value }))}
                  />
                </Field>
              </div>
            </Drawer>
          )}

          {/* 3. Sub contractor / Sub contractor labour Drawer */}
          {activeTab === 'subcontractors' && (
            <Drawer
              title={
                editingId
                  ? subcontractorDraft.entryMode === 'individual'
                    ? 'Edit Individual Work Entry'
                    : 'Edit Weekly Subcontractor Entry'
                  : subcontractorDraft.entryMode === 'individual'
                  ? 'Record Individual Work & Payment'
                  : 'Record Subcontractor Labour & Payment'
              }
              onClose={() => setDrawerOpen(false)}
              onSubmit={handleSubmit}
              submitLabel={
                editingId
                  ? 'Save Changes'
                  : subcontractorDraft.entryMode === 'individual'
                  ? 'Save Individual Entry'
                  : 'Save Labour Entry & Payment'
              }
              busy={isSaving}
              style={{ width: 'min(860px, 95vw)' }}
            >
              {/* Mode Selector: Weekly Payment (Default) vs Individual Payment */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: 4,
                  background: '#f1f5f9',
                  borderRadius: 10,
                  marginBottom: 16,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if ((subcontractorDraft.entryMode || 'weekly') !== 'weekly') {
                      setSubcontractorDraft(createInitialSubcontractorDraft('weekly'));
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    borderRadius: 8,
                    border: 'none',
                    outline: 'none',
                    background: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '#ffffff' : 'transparent',
                    color: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '#1d4ed8' : '#64748b',
                    fontWeight: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? 700 : 600,
                    fontSize: 13.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: (subcontractorDraft.entryMode || 'weekly') === 'weekly' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Calendar size={16} strokeWidth={2.2} />
                  <span>Weekly Payment</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (subcontractorDraft.entryMode !== 'individual') {
                      setSubcontractorDraft(createInitialSubcontractorDraft('individual'));
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    borderRadius: 8,
                    border: 'none',
                    outline: 'none',
                    background: subcontractorDraft.entryMode === 'individual' ? '#ffffff' : 'transparent',
                    color: subcontractorDraft.entryMode === 'individual' ? '#0e7490' : '#64748b',
                    fontWeight: subcontractorDraft.entryMode === 'individual' ? 700 : 600,
                    fontSize: 13.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: subcontractorDraft.entryMode === 'individual' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Layers size={16} strokeWidth={2.2} />
                  <span>Individual Payment</span>
                </button>
              </div>

              {/* Header Info: One by one, one field per row */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 16 }}>
                <Field label="Date">
                  <input
                    type="date"
                    value={subcontractorDraft.date}
                    onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </Field>

                <Field label="Select Client">
                  <Select
                    value={subcontractorDraft.clientId || ''}
                    onChange={handleSubcontractorClientChange}
                    options={lookups?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []}
                  />
                </Field>

                <Field label="Select Project">
                  <Select
                    value={subcontractorDraft.projectId}
                    onChange={handleSubcontractorProjectChange}
                    options={subcontractorProjects.map((p) => ({ value: p.id, label: p.name }))}
                  />
                </Field>

                <Field label="Select Sub contractor">
                  <Select
                    value={subcontractorDraft.subcontractorId}
                    onChange={(v) => {
                      if (v === '__new_subcontractor__') {
                        setShowQuickAddSub(true);
                        return;
                      }
                      setSubcontractorDraft((prev) => ({ ...prev, subcontractorId: v }));
                    }}
                    options={[
                      ...(subcontractorDraft.entryMode === 'individual'
                        ? [{ value: '__new_subcontractor__', label: '+ Add New Sub Contractor' }]
                        : []),
                      ...(lookups?.subcontractors?.map((s) => ({ value: s.id, label: s.name })) ?? []),
                    ]}
                  />
                </Field>

                {/* Quick Add Subcontractor Form (Individual mode alone) */}
                {subcontractorDraft.entryMode === 'individual' && showQuickAddSub && (
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                      border: '1.5px solid #93c5fd',
                      borderRadius: 10,
                      padding: '16px 18px',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.08)',
                      animation: 'fadeIn 0.15s ease-out',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 13.5, color: '#1e40af' }}>
                        <HardHat size={16} />
                        <span>Add New Sub Contractor</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowQuickAddSub(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#64748b',
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 700,
                          padding: '0 4px',
                        }}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <Field label="Sub Contractor Name">
                        <input
                          value={newSubName}
                          onChange={(e) => setNewSubName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleCreateQuickSub();
                            }
                          }}
                          autoFocus
                          required
                        />
                      </Field>

                      <Field label="Sub Contractor Phone Number">
                        <input
                          type="tel"
                          value={newSubPhone}
                          onChange={(e) => setNewSubPhone(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleCreateQuickSub();
                            }
                          }}
                          required
                        />
                      </Field>

                      <Field label="Location">
                        <input
                          value={newSubLocation}
                          onChange={(e) => setNewSubLocation(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleCreateQuickSub();
                            }
                          }}
                        />
                      </Field>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                        <button
                          type="button"
                          onClick={() => setShowQuickAddSub(false)}
                          style={{
                            padding: '8px 16px',
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#475569',
                            cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleCreateQuickSub()}
                          disabled={isCreatingSub || !newSubName.trim() || !newSubPhone.trim()}
                          style={{
                            padding: '8px 18px',
                            background: '#2563eb',
                            border: 'none',
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#ffffff',
                            cursor: isCreatingSub || !newSubName.trim() || !newSubPhone.trim() ? 'not-allowed' : 'pointer',
                            opacity: isCreatingSub || !newSubName.trim() || !newSubPhone.trim() ? 0.6 : 1,
                            boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
                          }}
                        >
                          {isCreatingSub ? 'Saving...' : 'Save Sub Contractor'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* =========================================================================
                  A. WEEKLY PAYMENT WORKFLOW
                  ========================================================================= */}
              {(subcontractorDraft.entryMode || 'weekly') === 'weekly' && (
                <>
                  {/* Subcontractor Contract & Weekly Balance Summary (Project Specific, Weekly mode only) */}
                  {(currentSubcontractorContract || (subcontractorDraft.projectId && priorSubcontractorBalanceRupees > 0)) && (
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                        border: '1px solid #bfdbfe',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginBottom: 14,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <HardHat size={15} color="#2563eb" />
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            Sub Contractor Project Contract
                          </span>
                        </div>
                        {currentSubcontractorContract && (
                          <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: '#dbeafe', color: '#1e40af', textTransform: 'uppercase' }}>
                            {currentSubcontractorContract.status || 'Ongoing'}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            currentSubcontractorContract && priorSubcontractorBalanceRupees > 0
                              ? 'repeat(auto-fit, minmax(min(100%, 120px), 1fr))'
                              : currentSubcontractorContract
                              ? 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))'
                              : '1fr',
                          gap: 10,
                        }}
                      >
                        {currentSubcontractorContract && (
                          <>
                            <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}>Total Amount</div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                                {formatMoney(currentSubcontractorContract.contractAmountPaise)}
                              </div>
                            </div>
                            <div style={{ background: '#ffffff', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}>Need to Pay</div>
                              <div
                                style={{
                                  fontSize: 15,
                                  fontWeight: 800,
                                  color: currentSubcontractorContract.pendingAmountPaise > 0 ? '#ea580c' : '#15803d',
                                  fontFamily: 'var(--font-mono)',
                                  marginTop: 2,
                                }}
                              >
                                {formatMoney(currentSubcontractorContract.pendingAmountPaise)}
                              </div>
                            </div>
                          </>
                        )}
                        {priorSubcontractorBalanceRupees > 0 && (
                          <div style={{ background: '#fffbeb', padding: '8px 10px', borderRadius: 6, border: '1px solid #fde68a' }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#92400e' }}>Weekly Balance</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#b45309', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                              ₹{priorSubcontractorBalanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Work Types & Weekly Rate Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <HardHat size={18} strokeWidth={2.2} style={{ color: '#0f172a' }} />
                    <h4 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                      Work Types & Weekly Rate
                    </h4>
                  </div>

                      {/* Work Types Card Container */}
                      <div
                        style={{
                          background: '#ffffff',
                          borderRadius: 10,
                          border: '1px solid #e2e8f0',
                          padding: '16px 18px',
                          marginBottom: 18,
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                        }}
                      >
                        {/* Column Headers: WORK NAME and WEEKLY RATE (₹) (Desktop Only) */}
                        <div className="subcontractor-table-header">
                          <div>WORK NAME</div>
                          <div>WEEKLY RATE (₹)</div>
                          <div />
                        </div>

                        {/* Line Items */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 10 }}>
                          {subcontractorDraft.lines.map((line, idx) => (
                            <div key={idx} className="subcontractor-line-item">
                              {/* 1. Work Name Selector + Remove Button on mobile */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <label className="subcontractor-line-mobile-label">
                                    Work Name
                                  </label>
                                  {line.is_custom ? (
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                      <input
                                        type="text"
                                        autoFocus
                                        value={line.custom_work_name || ''}
                                        onChange={(e) => handleSubcontractorLineChange(idx, 'custom_work_name', e.target.value)}
                                        style={{
                                          width: '100%',
                                          height: 38,
                                          background: '#ffffff',
                                          border: '1px solid #3b82f6',
                                          borderRadius: 6,
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: '#0f172a',
                                          padding: '0 30px 0 10px',
                                          boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.15)',
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleSubcontractorLineChange(idx, 'is_custom', false);
                                          handleSubcontractorLineChange(idx, 'work_type_id', '');
                                          handleSubcontractorLineChange(idx, 'custom_work_name', '');
                                        }}
                                        style={{
                                          position: 'absolute',
                                          right: 6,
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          color: '#94a3b8',
                                          padding: 4,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                        title="Switch back to dropdown"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <Select
                                      size="sm"
                                      value={line.work_type_id}
                                      options={subcontractorWorkTypeOptions}
                                      onChange={(val) => {
                                        const selectedOpt = subcontractorWorkTypeOptions.find((o) => o.value === val);
                                        const isOthers = val === '__other__' || selectedOpt?.label?.trim().toLowerCase() === 'others';
                                        if (isOthers) {
                                          handleSubcontractorLineChange(idx, 'is_custom', true);
                                          handleSubcontractorLineChange(idx, 'work_type_id', val);
                                          handleSubcontractorLineChange(idx, 'custom_work_name', '');
                                        } else {
                                          handleSubcontractorLineChange(idx, 'work_type_id', val);
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeSubcontractorLine(idx)}
                                  disabled={subcontractorDraft.lines.length <= 1}
                                  className="subcontractor-remove-btn-mobile"
                                  title="Remove line"
                                >
                                  <Trash2 size={16} strokeWidth={1.8} />
                                </button>
                              </div>

                              {/* 2. Weekly Rate */}
                              <div style={{ width: '100%' }}>
                                <label className="subcontractor-line-mobile-label">
                                  Weekly Rate (₹)
                                </label>
                                <input
                                  type="number"
                                  step="any"
                                  value={line.daily_rate}
                                  onChange={(e) => handleSubcontractorLineChange(idx, 'daily_rate', e.target.value)}
                                  style={{
                                    width: '100%',
                                    height: 38,
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    fontFamily: 'var(--font-mono)',
                                    color: '#0f172a',
                                    padding: '0 10px',
                                  }}
                                />
                              </div>

                              {/* 3. Remove Line Button (Desktop Only) */}
                              <div className="subcontractor-remove-btn-desktop">
                                <button
                                  type="button"
                                  onClick={() => removeSubcontractorLine(idx)}
                                  disabled={subcontractorDraft.lines.length <= 1}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'none',
                                    border: 'none',
                                    cursor: subcontractorDraft.lines.length <= 1 ? 'not-allowed' : 'pointer',
                                    color: '#94a3b8',
                                    borderRadius: 4,
                                    opacity: subcontractorDraft.lines.length <= 1 ? 0.3 : 1,
                                    transition: 'color 0.15s ease',
                                  }}
                                  title="Remove line"
                                >
                                  <Trash2 size={16} strokeWidth={1.8} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add Another Work Type Button */}
                        <div style={{ marginTop: 14 }}>
                          <button
                            type="button"
                            onClick={addSubcontractorLine}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              background: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              color: '#2563eb',
                              borderRadius: 8,
                              padding: '8px 16px',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <Plus size={15} strokeWidth={2.4} />
                            <span>Add Another Work Type</span>
                          </button>
                        </div>
                      </div>

                      {/* Debit & Credit Section */}
                      <div
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          padding: '12px 14px',
                          marginBottom: 14,
                        }}
                      >
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                          Debit & Credit
                        </div>

                        <div className="grid3" style={{ gap: 10 }}>
                          <Field label="Credit (₹)">
                            <div
                              style={{
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 10px',
                                borderRadius: 6,
                                border: '1px solid #e2e8f0',
                                background: '#ffffff',
                                fontWeight: 700,
                                fontSize: 13,
                                fontFamily: 'var(--font-mono)',
                                color: '#0f172a',
                                width: '100%',
                              }}
                            >
                              ₹{subcontractorOverallTotalRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </Field>

                          <Field label="Debit (₹)" optional>
                            <input
                              type="number"
                              step="any"
                              value={subcontractorDraft.paidAmountRupees}
                              onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, paidAmountRupees: e.target.value }))}
                              style={{
                                width: '100%',
                                height: 34,
                                fontWeight: 600,
                                fontFamily: 'var(--font-mono)',
                                color: '#059669',
                                border: '1px solid #e2e8f0',
                                borderRadius: 6,
                                padding: '0 10px',
                                background: '#ffffff',
                                fontSize: 13,
                              }}
                            />
                          </Field>

                          <Field label="Total Balance (₹)">
                            <div
                              style={{
                                height: 34,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 10px',
                                borderRadius: 6,
                                border: totalSubcontractorBalanceToGiveRupees === 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                                background: totalSubcontractorBalanceToGiveRupees === 0 ? '#f0fdf4' : '#fef2f2',
                                color: totalSubcontractorBalanceToGiveRupees === 0 ? '#15803d' : '#dc2626',
                                fontWeight: 700,
                                fontSize: 13,
                                fontFamily: 'var(--font-mono)',
                                width: '100%',
                              }}
                            >
                              ₹{totalSubcontractorBalanceToGiveRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          </Field>
                        </div>

                        {/* Total Balance (Previous Balance + This Bill) Banner */}
                        <div
                          style={{
                            marginTop: 10,
                            padding: '8px 12px',
                            borderRadius: 6,
                            background: totalSubcontractorBalanceToGiveRupees > 0 ? '#eff6ff' : '#f0fdf4',
                            border: totalSubcontractorBalanceToGiveRupees > 0 ? '1px solid #bfdbfe' : '1px solid #bbf7d0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 10,
                          }}
                        >
                          <div style={{ fontSize: 11, fontWeight: 700, color: totalSubcontractorBalanceToGiveRupees > 0 ? '#1e40af' : '#166534', letterSpacing: '0.02em' }}>
                            {netSubcontractorBalanceToGiveRupees < 0
                              ? 'ADVANCE / SURPLUS TO SUB CONTRACTOR'
                              : 'TOTAL BALANCE (PREVIOUS BALANCE + THIS BILL)'}
                          </div>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 800,
                              fontFamily: 'var(--font-mono)',
                              color: totalSubcontractorBalanceToGiveRupees > 0 ? '#1d4ed8' : '#15803d',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {netSubcontractorBalanceToGiveRupees < 0
                              ? `₹${excessSubcontractorAdvanceRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Advance)`
                              : `₹${totalSubcontractorBalanceToGiveRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </div>
                        </div>
                      </div>

                  {/* Mode & Remarks */}
                  <div className="grid2" style={{ gap: 14 }}>
                    <Field label="Mode of Payment">
                      <Select
                        value={subcontractorDraft.mode}
                        onChange={(v) => setSubcontractorDraft((prev) => ({ ...prev, mode: v }))}
                        options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
                      />
                    </Field>

                    <Field label="Remarks" optional>
                      <input
                        value={subcontractorDraft.remarks}
                        onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, remarks: e.target.value }))}
                      />
                    </Field>
                  </div>
                </>
              )}

              {/* =========================================================================
                  B. INDIVIDUAL PAYMENT WORKFLOW
                  ========================================================================= */}
              {subcontractorDraft.entryMode === 'individual' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Multiple Individual Work Lines: 1 row 4 column order, responsive */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(subcontractorDraft.individualLines && subcontractorDraft.individualLines.length > 0
                      ? subcontractorDraft.individualLines
                      : [
                          {
                            nameOfWork: subcontractorDraft.nameOfWork || '',
                            workVolume: subcontractorDraft.workVolume || '',
                            unit: subcontractorDraft.unit || 'Sq.ft',
                            rate: subcontractorDraft.rate || '',
                          },
                        ]
                    ).map((item, idx) => {
                      const hasMultiple = (subcontractorDraft.individualLines?.length ?? 1) > 1;
                      return (
                        <div
                          key={idx}
                          className={`individual-work-item ${!hasMultiple ? 'no-delete' : ''}`}
                        >
                          {/* Column 1: Name of Work */}
                          <div className="work-col-field work-col-name">
                            <Field label="Name of Work">
                              <input
                                value={item.nameOfWork || ''}
                                onChange={(e) => handleIndividualLineChange(idx, 'nameOfWork', e.target.value)}
                                required
                              />
                            </Field>
                          </div>

                          {/* Column 2: Work Volume / Quantity */}
                          <div className="work-col-field work-col-volume">
                            <Field label="Work Volume / Quantity">
                              <input
                                type="number"
                                step="any"
                                min="0.01"
                                value={item.workVolume || ''}
                                onChange={(e) => handleIndividualLineChange(idx, 'workVolume', e.target.value)}
                                style={{
                                  fontWeight: 700,
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: 14,
                                }}
                                required
                              />
                            </Field>
                          </div>

                          {/* Column 3: Unit */}
                          <div className="work-col-field work-col-unit">
                            <Field label="Unit">
                              <Select
                                value={item.unit || 'Sq.ft'}
                                onChange={(v) => handleIndividualLineChange(idx, 'unit', v)}
                                options={INDIVIDUAL_UNITS}
                              />
                            </Field>
                          </div>

                          {/* Column 4: Rate per Unit (₹) */}
                          <div className="work-col-field work-col-rate">
                            <Field label="Rate per Unit (₹)">
                              <input
                                type="number"
                                step="any"
                                min="0.01"
                                value={item.rate || ''}
                                onChange={(e) => handleIndividualLineChange(idx, 'rate', e.target.value)}
                                style={{
                                  fontWeight: 700,
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: 14,
                                }}
                                required
                              />
                            </Field>
                          </div>

                          {/* Action Button: Remove Line if multiple */}
                          {hasMultiple && (
                            <div className="work-col-action">
                              <button
                                type="button"
                                onClick={() => removeIndividualLine(idx)}
                                style={{
                                  width: 38,
                                  height: 38,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'none',
                                  border: '1px solid #e2e8f0',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  color: '#94a3b8',
                                  transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = '#ef4444';
                                  e.currentTarget.style.borderColor = '#fca5a5';
                                  e.currentTarget.style.background = '#fef2f2';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = '#94a3b8';
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.background = 'none';
                                }}
                                title="Remove worker detail"
                              >
                                <Trash2 size={16} strokeWidth={1.8} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Add Another Worker Detail Button */}
                  <div style={{ marginTop: 2, marginBottom: 4 }}>
                    <button
                      type="button"
                      onClick={addIndividualLine}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#2563eb',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#dbeafe';
                        e.currentTarget.style.borderColor = '#93c5fd';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#eff6ff';
                        e.currentTarget.style.borderColor = '#bfdbfe';
                      }}
                    >
                      <Plus size={15} strokeWidth={2.4} />
                      <span>Add Another Worker Detail</span>
                    </button>
                  </div>

                  {/* Debit & Credit Section */}
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '12px 14px',
                      marginBottom: 14,
                      marginTop: 12,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                      Debit & Credit
                    </div>

                    <div className="grid3" style={{ gap: 10 }}>
                      <Field label="Credit (₹)">
                        <div
                          style={{
                            height: 34,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                            borderRadius: 6,
                            border: '1px solid #e2e8f0',
                            background: '#ffffff',
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: 'var(--font-mono)',
                            color: '#0f172a',
                            width: '100%',
                          }}
                        >
                          ₹{individualTotalRupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </Field>

                      <Field label="Debit (₹)" optional>
                        <input
                          type="number"
                          step="any"
                          value={subcontractorDraft.paidAmountRupees}
                          onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, paidAmountRupees: e.target.value }))}
                          style={{
                            width: '100%',
                            height: 34,
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            color: '#059669',
                            border: '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: '0 10px',
                            background: '#ffffff',
                            fontSize: 13,
                          }}
                        />
                      </Field>

                      <Field label="This Bill Balance (₹)">
                        <div
                          style={{
                            height: 34,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 10px',
                            borderRadius: 6,
                            border: indBalanceNum === 0 ? '1px solid #bbf7d0' : '1px solid #fecaca',
                            background: indBalanceNum === 0 ? '#f0fdf4' : '#fef2f2',
                            color: indBalanceNum === 0 ? '#15803d' : '#dc2626',
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: 'var(--font-mono)',
                            width: '100%',
                          }}
                        >
                          ₹{indBalanceNum.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </Field>
                    </div>
                  </div>

                  {/* Mode of Payment & Remarks */}
                  <div className="grid2" style={{ gap: 14 }}>
                    <Field label="Mode of Payment">
                      <Select
                        value={subcontractorDraft.mode}
                        onChange={(v) => setSubcontractorDraft((prev) => ({ ...prev, mode: v }))}
                        options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
                      />
                    </Field>

                    <Field label="Remarks" optional>
                      <input
                        value={subcontractorDraft.remarks}
                        onChange={(e) => setSubcontractorDraft((prev) => ({ ...prev, remarks: e.target.value }))}
                      />
                    </Field>
                  </div>
                </div>
              )}
            </Drawer>
          )}

          {/* 4. Other Expenses / Add Expense Drawer */}
          {activeTab === 'expenses' && (
            <Drawer
              title={editingId ? 'Edit Expense' : 'Add Expense'}
              onClose={() => setDrawerOpen(false)}
              onSubmit={handleSubmit}
              submitLabel={editingId ? 'Save Changes' : 'Save Expense'}
              busy={isSaving}
            >
              {/* Header Info: Date 1st, Select Client 2nd, Select Project 3rd, Expense Category 4th */}
              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label="Date">
                  <input
                    type="date"
                    value={expenseDraft.date}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, date: e.target.value }))}
                    required
                  />
                </Field>

                <Field label="Select Client">
                  <Select
                    value={expenseDraft.clientId || ''}
                    onChange={handleExpenseClientChange}
                    options={lookups?.clients?.map((c) => ({ value: c.id, label: c.name })) ?? []}
                    placeholder="Choose client…"
                  />
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginBottom: 14 }}>
                <Field label="Select Project">
                  <Select
                    value={expenseDraft.projectId}
                    onChange={handleExpenseProjectChange}
                    options={expenseProjects.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Choose project…"
                  />
                </Field>

                <Field label={expenseDraft.categoryId === '__other__' ? 'Expense Category (Others)' : 'Expense Category'}>
                  {expenseDraft.categoryId === '__other__' ? (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <input
                        type="text"
                        autoFocus
                        value={expenseDraft.customCategory || ''}
                        onChange={(e) => setExpenseDraft((prev) => ({ ...prev, customCategory: e.target.value }))}
                        placeholder="Type expense category…"
                        required
                        style={{
                          width: '100%',
                          paddingRight: 75,
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setExpenseDraft((prev) => ({ ...prev, categoryId: '', customCategory: '' }))}
                        title="Back to category list"
                        style={{
                          position: 'absolute',
                          right: 6,
                          fontSize: 11,
                          padding: '3px 8px',
                          borderRadius: 4,
                          background: 'var(--surface-3, #f1f5f9)',
                          color: 'var(--text-soft, #475569)',
                          border: '1px solid var(--border, #cbd5e1)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          fontWeight: 600,
                        }}
                      >
                        ✕ List
                      </button>
                    </div>
                  ) : (
                    <Select
                      value={expenseDraft.categoryId}
                      onChange={(v) => {
                        if (v === '__other__') {
                          setExpenseDraft((prev) => ({ ...prev, categoryId: '__other__', customCategory: '' }));
                        } else {
                          setExpenseDraft((prev) => ({ ...prev, categoryId: v, customCategory: '' }));
                        }
                      }}
                      options={[
                        ...(lookups?.expenseCategories?.map((c) => ({ value: c.id, label: c.name })) ?? []),
                        { value: '__other__', label: 'Others' },
                      ]}
                      placeholder="Choose category…"
                    />
                  )}
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginTop: 14 }}>
                <Field label="Amount (₹)">
                  <input
                    type="number"
                    step="any"
                    value={expenseDraft.amountRupees}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, amountRupees: e.target.value }))}
                    placeholder="0.00"
                    required
                  />
                </Field>

                <Field label="Paid To" optional>
                  <input
                    value={expenseDraft.paidTo}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, paidTo: e.target.value }))}
                    placeholder="Recipient / Vendor name"
                  />
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginTop: 14 }}>
                <Field label="Mode of Payment">
                  <Select
                    value={expenseDraft.mode}
                    onChange={(v) => setExpenseDraft((prev) => ({ ...prev, mode: v }))}
                    options={PAYMENT_MODES.map((m) => ({ value: m.label, label: m.label }))}
                  />
                </Field>
              </div>

              <div className="grid2" style={{ gap: 14, marginTop: 14 }}>
                <Field label="Bill / Voucher No" optional>
                  <input
                    value={expenseDraft.billNo}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, billNo: e.target.value }))}
                  />
                </Field>

                <Field label="Bill or Invoice Date" optional>
                  <input
                    type="date"
                    value={expenseDraft.billDate || expenseDraft.date}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, billDate: e.target.value }))}
                  />
                </Field>
              </div>

              <div style={{ marginTop: 14 }}>
                <Field label="Remarks" optional>
                  <input
                    value={expenseDraft.remarks}
                    onChange={(e) => setExpenseDraft((prev) => ({ ...prev, remarks: e.target.value }))}
                  />
                </Field>
              </div>
            </Drawer>
          )}
        </>
      )}

      {confirmDelete.target && (
        <Confirm
          title="Confirm Delete"
          message={`Are you sure you want to permanently delete this ${confirmDelete.target.label}? This entry and any attached records will be deleted from the database and storage.`}
          confirmLabel="Delete"
          danger
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={confirmDelete.close}
        />
      )}
    </Shell>
  );
}
